/**
 * AI Planning V1 – Planning chat page
 * Split layout: left = conversation, right = roadmap tree (vision → objectives → strategies → projects).
 * Main planning workflow: left = chat; right = Preview (draft tree + Save / Download).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { buildObjectiveName } from '@/components/alignment/objectiveName';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  LightBulbIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/state/auth/AuthProvider';
import {
  createSession,
  postMessageStream,
  getDraft,
  loadSession,
  listSessions,
  deleteSession,
  saveToWorkspace,
  flagPendingSave,
  downloadSession,
  downloadSessionWithChart,
  uploadRoadmap,
  attachDocument,
  updateSessionName,
  fetchPlanningUsageSummary,
  isPlanningQuotaPayload,
  getPlanningCollabState,
  type DraftSnapshot,
  type PlanningSessionSummary,
  type PlanningUsageSummary,
} from '@/services/planningService';
import { type RoadmapProject } from '@/services/roadmapMarketplace';
import api from '@/services/api';
import VisualRoadmapTimeline from '@/components/roadmap/VisualRoadmapTimeline';
import { PlanningCollabToolbar } from '@/components/ai/PlanningCollabToolbar';
import { PlanningAnonAuthBar } from '@/components/ai/PlanningAnonAuthBar';
import { PLANNING_INPUT_EXAMPLE_PROMPTS } from '@/pages/ai/planningInputExamplePrompts';
import { getUserProfilePictureUrl, resolveProfileImageUrl } from '@/utils/profileImageUrl';
import { authService } from '@/services/auth';

const QUOTA_EXHAUSTED_MARKER = '__QUOTA_EXHAUSTED__';

const PLANNING_HINT_HOLD_MS = 2000;
/** ~200ms ease-in + 2s visible before the next example (must match `.animate-planning-hint-enter` in index.css). */
const PLANNING_HINT_STEP_MS = 200 + PLANNING_HINT_HOLD_MS;

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  userId?: string | null;
  userDisplayName?: string | null;
  userAvatarUrl?: string | null;
  /** Only set for quota-exhausted assistant message */
  limitDebug?: { cap: number; used: number };
  /** Paid / period quota: show API message instead of generic free-tier copy */
  serverQuotaMessage?: string;
}

function mapApiMessageToChat(m: {
  id: string;
  role: string;
  content: string;
  createdAt: string;
  userId?: string | null;
  userDisplayName?: string | null;
  userAvatarUrl?: string | null;
}): ChatMessage {
  return {
    id: m.id,
    role: (m.role === 'user' ? 'user' : 'assistant') as 'user' | 'assistant',
    content: m.content,
    createdAt: m.createdAt,
    userId: m.userId ?? null,
    userDisplayName: m.userDisplayName ?? null,
    userAvatarUrl: m.userAvatarUrl ?? null,
  };
}

function userInitials(name: string | null | undefined): string {
  const n = (name || '').trim();
  if (!n) return '?';
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase();
  return n.slice(0, 2).toUpperCase();
}

/** Circular avatar; title/hover shows display name for multi-user chats. */
const PlanningUserAvatar: React.FC<{
  displayName?: string | null;
  avatarUrl?: string | null;
}> = ({ displayName, avatarUrl }) => {
  const label = (displayName || '').trim() || 'User';
  const src = avatarUrl?.trim() || null;
  const [imgFailed, setImgFailed] = React.useState(false);
  React.useEffect(() => {
    setImgFailed(false);
  }, [src]);
  return (
    <div
      className="h-8 w-8 shrink-0 self-start rounded-full overflow-hidden border border-[var(--color-border)] bg-[var(--color-secondary-bg)] flex items-center justify-center text-xs font-semibold text-[var(--color-surface-text)]"
      title={label}
      aria-label={label}
    >
      {src && !imgFailed ? (
        <img
          src={src}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span aria-hidden>{userInitials(displayName)}</span>
      )}
    </div>
  );
};

function isQuotaExhaustedMessage(m: ChatMessage): m is ChatMessage & { limitDebug?: { cap: number; used: number } } {
  return m.role === 'assistant' && m.content === QUOTA_EXHAUSTED_MARKER;
}

/** Normalize draft field to array of items. */
function toArray(x: Record<string, unknown> | unknown[] | null | undefined): Record<string, unknown>[] {
  if (x == null) return [];
  if (Array.isArray(x)) return x.filter((i): i is Record<string, unknown> => i != null && typeof i === 'object');
  if (typeof x === 'object') {
    const o = x as Record<string, unknown>;
    if (Array.isArray(o.items)) return o.items as Record<string, unknown>[];
    return Object.values(o).filter((i): i is Record<string, unknown> => i != null && typeof i === 'object');
  }
  return [];
}

function nodeTitle(item: Record<string, unknown>): string {
  const t = item['title'] ?? item['name'] ?? item['summary'];
  return t != null ? String(t) : 'Untitled';
}

function titleNorm(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * True when objectives carry nested strategies (collaborative draft shape).
 * After bake, draft.projects is also a flattened denormalized copy of nested projects —
 * that copy must NOT be shown again as "unassigned".
 */
function draftHasNestedTree(draft: DraftSnapshot | null | undefined): boolean {
  if (!draft) return false;
  const objectivesRaw = toArray(draft.objectives as Record<string, unknown> | null);
  return objectivesRaw.length > 0 && typeof objectivesRaw[0]?.strategies !== 'undefined';
}

/** Titles of projects already nested under objective → strategy. */
function nestedProjectTitleNorms(draft: DraftSnapshot): Set<string> {
  const norms = new Set<string>();
  for (const obj of toArray(draft.objectives as Record<string, unknown> | null)) {
    for (const strat of toArray(obj.strategies as Record<string, unknown>)) {
      for (const proj of toArray(strat.projects as Record<string, unknown>)) {
        const t = nodeTitle(proj);
        if (t && t !== 'Untitled') norms.add(titleNorm(t));
      }
    }
  }
  return norms;
}

/**
 * Flat draft.projects that are true orphans (not already in the nested tree).
 * When the tree is nested, the flat array is usually a bake-time mirror — exclude duplicates.
 */
function orphanRootProjects(draft: DraftSnapshot | null | undefined): Record<string, unknown>[] {
  if (!draft) return [];
  const flat = toArray(draft.projects as Record<string, unknown> | null);
  if (!draftHasNestedTree(draft)) return flat;
  const nested = nestedProjectTitleNorms(draft);
  if (nested.size === 0) return flat;
  return flat.filter((p) => {
    const t = nodeTitle(p);
    if (!t || t === 'Untitled') return true;
    return !nested.has(titleNorm(t));
  });
}

/** Format date for display: "Mar 4, 2026" or "today at 2:30 PM" for same day */
function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return `today at ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Extract user-facing message from API/axios error (response.data.message, validation errors, or network message). */
function getApiErrorMessage(e: unknown, fallback: string): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const res = (e as {
      response?: { status?: number; data?: { message?: string; error?: string; errors?: Array<{ msg?: string }> } };
    }).response;
    const data = res?.data;
    if (data?.message) return data.message;
    if (typeof data?.error === 'string' && data.error) return data.error;
    const errs = data?.errors;
    if (Array.isArray(errs) && errs.length > 0) {
      const first = errs[0]?.msg ?? (errs[0] as { message?: string })?.message;
      if (first) return first;
    }
  }
  const raw = e instanceof Error ? e.message : fallback;
  if (/network error/i.test(raw) || /^failed to fetch$/i.test(raw)) {
    return 'We could not reach the server. Check your connection and try again. If you are out of AI tokens, open Subscription in your profile.';
  }
  return raw;
}

/** Collect all number/target fields from an objective for display (matches backend). */
function objectiveNumbers(obj: Record<string, unknown>): string[] {
  const keys = [
    'target',
    'targetNumber',
    'targetValue',
    'target_value',
    'kpi',
    'metric',
    'value',
    'baseline',
    'current',
    'number',
    'percentage',
    'count',
    'amount',
    'goal',
  ];
  const labels: Record<string, string> = {
    target: 'Target',
    targetNumber: 'Target',
    targetValue: 'Target',
    target_value: 'Target',
    kpi: 'KPI',
    metric: 'Metric',
    value: 'Value',
    baseline: 'Baseline',
    current: 'Current',
    number: 'Number',
    percentage: 'Percentage',
    count: 'Count',
    amount: 'Amount',
    goal: 'Goal',
  };
  const added = new Set<string>();
  const parts: string[] = [];
  for (const k of keys) {
    const v = obj[k];
    if (v != null && String(v).trim() !== '') {
      const label = labels[k] ?? k.charAt(0).toUpperCase() + k.slice(1);
      parts.push(`${label}: ${v}`);
      added.add(k);
    }
  }
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === 'number' && !Number.isNaN(v) && !added.has(k)) {
      const label = labels[k] ?? k.charAt(0).toUpperCase() + k.slice(1);
      parts.push(`${label}: ${v}`);
    }
  }
  return parts;
}

/** Label for an objective: prefer the specialist's board sentence; formula is a fallback. */
function objectiveLabel(obj: Record<string, unknown>): string {
  const title = nodeTitle(obj);
  if (title && title !== 'Untitled' && !/^objective\s*\d*$/i.test(title)) {
    return title;
  }
  const rawKpiValue = obj['kpi_value'] ?? obj['value'];
  const generated = buildObjectiveName({
    valueType: typeof obj['value_type'] === 'string' ? obj['value_type'] : null,
    kpiValue: typeof rawKpiValue === 'number' || typeof rawKpiValue === 'string' ? rawKpiValue : null,
    kpiName: typeof obj['kpi'] === 'string' ? obj['kpi'] : null,
    year: typeof obj['year'] === 'number' ? obj['year'] : Number(obj['year']) || null
  });
  if (generated) {
    return generated;
  }
  const nums = objectiveNumbers(obj);
  return nums.length > 0 ? `${title} (${nums.join(', ')})` : title;
}

/** Stakeholders are positions (e.g. VP Marketing), not person names. */
type StakeholderEntry = { position?: string; role?: string; name?: string };

function toStakeholders(arr: unknown): StakeholderEntry[] {
  if (!Array.isArray(arr)) return [];
  return arr
    .filter((i): i is Record<string, unknown> => i != null && typeof i === 'object')
    .map((i) => ({
      position: i['position'] != null ? String(i['position']) : undefined,
      role: i['role'] != null ? String(i['role']) : undefined,
      name: i['name'] != null ? String(i['name']) : undefined,
    }))
    .filter((s) => s.position || s.role || s.name);
}

/** Display label for a stakeholder (position preferred). */
function stakeholderLabel(s: StakeholderEntry): string {
  return (s.position || s.role || s.name || '').trim();
}

function projectStartDate(proj: Record<string, unknown>): string | null {
  const v = proj['start_date'] ?? proj['startDate'] ?? proj['start'];
  return v != null ? String(v).trim() : null;
}

function projectEndDate(proj: Record<string, unknown>): string | null {
  const v = proj['end_date'] ?? proj['endDate'] ?? proj['end'];
  return v != null ? String(v).trim() : null;
}

function projectDurationMonths(proj: Record<string, unknown>): number | null {
  const v = proj['duration_months'] ?? proj['durationMonths'];
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (v != null) {
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return null;
}

/** Months from portfolio start (0 = current month). Used when calendar dates are absent. */
function projectStartMonthOffset(proj: Record<string, unknown>): number | null {
  const v = proj['start_month'] ?? proj['startMonth'];
  if (typeof v === 'number' && !Number.isNaN(v) && v >= 0) return Math.round(v);
  if (v != null) {
    const n = Number(v);
    if (!Number.isNaN(n) && n >= 0) return Math.round(n);
  }
  return null;
}

function currentCalendarMonthIndex(): number {
  const now = new Date();
  const iso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  return monthIndexFromDate(iso) ?? 0;
}

function projectCost(proj: Record<string, unknown>): string | null {
  const v = proj['cost'] ?? proj['estimated_cost'];
  if (v == null) return null;
  if (typeof v === 'string') return v.trim() || null;
  if (typeof v === 'number') return String(v);
  return null;
}

function projectStakeholders(proj: Record<string, unknown>): StakeholderEntry[] {
  return toStakeholders(proj['stakeholders']);
}

function projectObjectives(proj: Record<string, unknown>): string[] {
  const raw = proj['objectives'] ?? proj['success_criteria'];
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((o) => {
        if (o == null) return '';
        if (typeof o === 'string') return o.trim();
        if (typeof o === 'object') {
          const obj = o as Record<string, unknown>;
          const text = (obj['title'] ?? obj['name'] ?? obj['text']) as string | undefined;
          return text != null ? String(text).trim() : '';
        }
        return String(o).trim();
      })
      .filter(Boolean);
  }
  if (typeof raw === 'string') return raw.trim() ? [raw.trim()] : [];
  return [];
}

function projectRisks(proj: Record<string, unknown>): string[] {
  const raw = proj['risks'];
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((r) => {
        if (r == null) return '';
        if (typeof r === 'string') return r.trim();
        if (typeof r === 'object') {
          const o = r as Record<string, unknown>;
          const text = (o['description'] ?? o['title'] ?? o['name'] ?? o['risk'] ?? o['text']) as string | undefined;
          return text != null ? String(text).trim() : '';
        }
        return String(r).trim();
      })
      .filter(Boolean);
  }
  if (typeof raw === 'string') return raw.trim() ? [raw.trim()] : [];
  return [];
}

/** Parse orchestrator warning from assistant content; returns { warning, rest } or null if none. */
function parseOrchestratorWarning(content: string): { warning: string; rest: string } | null {
  const start = '<!--ORCHESTRATOR_WARNING-->';
  const end = '<!--/ORCHESTRATOR_WARNING-->';
  const idx = content.indexOf(start);
  if (idx === -1) return null;
  const endIdx = content.indexOf(end, idx);
  if (endIdx === -1) return null;
  const warning = content.slice(idx + start.length, endIdx).trim();
  const rest = content.slice(endIdx + end.length).replace(/^\s*\n+/, '').trim();
  return { warning, rest };
}

/** Remove JSON from assistant text so the left panel shows only prose. Strips fenced blocks and trailing raw JSON (after a newline). */
function stripJsonBlocks(text: string): string {
  if (!text || typeof text !== 'string') return '';
  let out = text
    .replace(/\s*```(?:json)?\s*[\s\S]*?```\s*/g, '')
    .replace(/\s*```\s*[\s\S]*?```\s*/g, '')
    .trim();
  const trailingMatch = out.match(/\n\s*(\{[\s\S]*\}|\[[\s\S]*\])\s*$/);
  if (trailingMatch) {
    const candidate = trailingMatch[1].trim();
    if ((candidate.startsWith('{') && candidate.endsWith('}')) || (candidate.startsWith('[') && candidate.endsWith(']'))) {
      try {
        JSON.parse(candidate);
        out = out.slice(0, trailingMatch.index).trim();
      } catch {
        // not valid JSON, leave as is
      }
    }
  }
  return out || '';
}

/** Tree line characters for hierarchy */
const TREE = {
  branch: '├',
  last: '└',
  line: '│',
  dash: '─',
};

/** Colors for objective/strategy sections (match VisualRoadmapTimeline palette) */
const SECTION_COLORS = [
  { primary: '#3B82F6', secondary: '#DBEAFE', border: '#1E40AF' },
  { primary: '#10B981', secondary: '#D1FAE5', border: '#047857' },
  { primary: '#F59E0B', secondary: '#FEF3C7', border: '#D97706' },
  { primary: '#8B5CF6', secondary: '#EDE9FE', border: '#7C3AED' },
  { primary: '#EF4444', secondary: '#FEE2E2', border: '#DC2626' },
  { primary: '#06B6D4', secondary: '#CFFAFE', border: '#0891B2' },
];

const getSectionColor = (index: number) => SECTION_COLORS[index % SECTION_COLORS.length];

/** Month index from date string. Accepts YYYY-MM-DD, YYYY-M-D, YYYY-MM, or YYYY. Baseline: 2020-01 = 0. */
function monthIndexFromDate(iso: string): number | null {
  const s = String(iso).trim().replace(/^["']|["']$/g, '');
  // YYYY-MM-DD or YYYY-M-D (allow leading/trailing whitespace already trimmed)
  let match = s.match(/(\d{4})-(\d{1,2})(?:-(\d{1,2}))?/);
  if (match) {
    const y = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    if (!Number.isNaN(y) && !Number.isNaN(m) && m >= 1 && m <= 12) return (y - 2020) * 12 + (m - 1);
  }
  // YYYY only
  match = s.match(/(\d{4})/);
  if (match) {
    const y = parseInt(match[1], 10);
    if (!Number.isNaN(y)) return (y - 2020) * 12;
  }
  return null;
}

interface GanttProjectItem {
  id: string;
  title: string;
  strategyTitle: string | null;
  proj: Record<string, unknown>;
  startMonth: number;
  endMonth: number;
  color: { primary: string; border: string };
}

/** Collect all projects from draft for Gantt. Projects with dates use them; others get default placement (start 0, 3 months) so the chart always shows when there are projects. */
function collectDraftProjectsForGantt(draft: DraftSnapshot): {
  items: GanttProjectItem[];
  minMonth: number;
  totalMonths: number;
} {
  const items: GanttProjectItem[] = [];
  let minMonth = Number.MAX_SAFE_INTEGER;
  let maxMonth = Number.MIN_SAFE_INTEGER;
  let rowIndex = 0;
  const DEFAULT_DURATION_MONTHS = 3;

  function addProject(proj: Record<string, unknown>, strategyTitle: string | null) {
    const startStr = projectStartDate(proj);
    const endStr = projectEndDate(proj);
    const duration = projectDurationMonths(proj);
    const startOffset = projectStartMonthOffset(proj);
    const baseNow = currentCalendarMonthIndex();
    let startMonth: number;
    let endMonth: number;
    if (startStr && endStr) {
      const s = monthIndexFromDate(startStr);
      const e = monthIndexFromDate(endStr);
      if (s != null && e != null) {
        startMonth = s;
        endMonth = duration != null ? s + Math.max(1, duration) - 1 : e;
      } else if (duration != null && s != null) {
        startMonth = s;
        endMonth = s + Math.max(1, duration) - 1;
      } else if (startOffset != null) {
        startMonth = baseNow + startOffset;
        endMonth = startMonth + Math.max(1, duration ?? DEFAULT_DURATION_MONTHS) - 1;
      } else {
        startMonth = 0;
        endMonth = DEFAULT_DURATION_MONTHS - 1;
      }
    } else if (startStr) {
      const s = monthIndexFromDate(startStr);
      const dur = Math.max(1, duration ?? DEFAULT_DURATION_MONTHS);
      if (s != null) {
        startMonth = s;
        endMonth = s + dur - 1;
      } else if (startOffset != null) {
        startMonth = baseNow + startOffset;
        endMonth = startMonth + dur - 1;
      } else {
        startMonth = 0;
        endMonth = dur - 1;
      }
    } else if (startOffset != null) {
      startMonth = baseNow + startOffset;
      endMonth = startMonth + Math.max(1, duration ?? DEFAULT_DURATION_MONTHS) - 1;
    } else if (duration != null) {
      startMonth = 0;
      endMonth = Math.max(0, duration - 1);
    } else {
      startMonth = 0;
      endMonth = Math.max(0, DEFAULT_DURATION_MONTHS - 1);
    }

    minMonth = Math.min(minMonth, startMonth);
    maxMonth = Math.max(maxMonth, endMonth);
    const status = (proj['_status'] as string) || '';
    const statusColors: Record<string, { primary: string; secondary: string; border: string }> = {
      existing: { primary: '#6B7280', secondary: '#E5E7EB', border: '#4B5563' },
      new: { primary: '#2563EB', secondary: '#DBEAFE', border: '#1D4ED8' },
      changed: { primary: '#D97706', secondary: '#FEF3C7', border: '#B45309' },
    };
    const color = statusColors[status] ?? getSectionColor(rowIndex % SECTION_COLORS.length);
    items.push({
      id: `gantt-${items.length}-${nodeTitle(proj).slice(0, 20)}`,
      title: nodeTitle(proj),
      strategyTitle,
      proj,
      startMonth,
      endMonth,
      color: { primary: color.primary, border: color.border },
    });
    rowIndex += 1;
  }

  const objectivesRaw = toArray(draft.objectives as Record<string, unknown> | null);
  const hasNested = objectivesRaw.length > 0 && typeof objectivesRaw[0]?.strategies !== 'undefined';
  if (hasNested) {
    objectivesRaw.forEach((obj) => {
      toArray(obj.strategies as Record<string, unknown>).forEach((strat) => {
        const strategyTitle = nodeTitle(strat);
        toArray(strat.projects as Record<string, unknown>).forEach((p) => addProject(p, strategyTitle));
      });
    });
    // Only true orphans — flat draft.projects is often a denormalized copy after bake
    orphanRootProjects(draft).forEach((p) => addProject(p, null));
  } else {
    toArray(draft.projects as Record<string, unknown> | null).forEach((p) => addProject(p, null));
  }

  const totalMonths = Math.max(24, maxMonth - minMonth + 1);
  return { items, minMonth, totalMonths };
}

/** Map draft to RoadmapProject[] for VisualRoadmapTimeline (same Gantt as tenant roadmaps). Attaches _draftProject for hover tooltip. */
function mapDraftToTimelineProjects(draft: DraftSnapshot): { projects: (RoadmapProject & { _draftProject?: Record<string, unknown>; strategy_title?: string })[]; totalDuration: number } {
  const { items, minMonth, totalMonths } = collectDraftProjectsForGantt(draft);
  const now = new Date().toISOString();
  const placeholder: Omit<RoadmapProject, 'id' | 'name' | 'description' | 'start_month' | 'estimated_duration_months'> = {
    roadmap_template_id: '',
    objectives: [],
    expected_outcomes: [],
    complexity_level: 1,
    cost_drivers: {},
    cost_calculation_formula: '',
    status: 'PLANNING',
    hierarchy_type: 'PROJECT',
    industry: '',
    functional_area: '',
    transformation_type: '',
    tags: [],
    created_at: now,
    updated_at: now,
  };
  const projects = items.map((item) => {
    const startMonth = item.startMonth - minMonth;
    const duration = item.endMonth - item.startMonth + 1;
    const status = (item.proj['_status'] as string) || undefined;
    const proj: RoadmapProject & { _draftProject?: Record<string, unknown>; strategy_title?: string; _draftStatus?: 'existing' | 'new' | 'changed' } = {
      ...placeholder,
      id: item.id,
      name: item.title,
      description: (item.proj['description'] as string) ?? '',
      start_month: startMonth,
      estimated_duration_months: duration,
      strategy_title: item.strategyTitle ?? undefined,
      _draftProject: item.proj,
      _draftStatus: status === 'existing' || status === 'new' || status === 'changed' ? status : undefined,
    };
    return proj;
  });
  return { projects, totalDuration: totalMonths };
}

/** Single project card: title, optional dates/duration/cost/objectives/stakeholders/risks. */
function ProjectCard({
  proj,
  borderColor,
}: {
  proj: Record<string, unknown>;
  borderColor: string;
}) {
  const { t } = useLanguage();
  const title = nodeTitle(proj);
  const desc = proj['description'] != null ? String(proj['description']) : null;
  const start = projectStartDate(proj);
  const end = projectEndDate(proj);
  const duration = projectDurationMonths(proj);
  const cost = projectCost(proj);
  const objectives = projectObjectives(proj);
  const stakeholders = projectStakeholders(proj);
  const risks = projectRisks(proj);
  const hasMeta = start || end || duration != null || cost || objectives.length > 0 || stakeholders.length > 0 || risks.length > 0;

  return (
    <div
      className="rounded border bg-[var(--color-surface-bg)] p-2 text-sm"
      style={{ borderColor }}
    >
      <div className="font-medium text-[var(--color-surface-text)]">{title}</div>
      {desc && <p className="mt-0.5 text-[var(--color-surface-text-secondary)]">{desc}</p>}
      {hasMeta && (
        <div className="mt-1.5 space-y-0.5 text-[var(--color-surface-text-secondary)]">
          {(start || end) && (
            <div>{t('planning.start')}: {start ?? '—'} · {t('planning.end')}: {end ?? '—'}</div>
          )}
          {duration != null && <div>{t('planning.duration')}: {duration} {t('planning.months')}</div>}
          {cost && <div>{t('planning.cost')}: {cost}</div>}
          {objectives.length > 0 && (
            <div>
              {t('planning.objectivesLabel')}:
              <ul className="list-disc list-inside mt-0.5 ml-0">{objectives.map((o, i) => <li key={i}>{o}</li>)}</ul>
            </div>
          )}
          {stakeholders.length > 0 && (
            <div>
              {t('planning.stakeholders')}: {stakeholders.map((s) => stakeholderLabel(s)).filter(Boolean).join('; ')}
            </div>
          )}
          {risks.length > 0 && (
            <div>
              {t('planning.risks')}:
              <ul className="list-disc list-inside mt-0.5 ml-0">{risks.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Visual timeline-style view of the draft (vision → objectives → strategies → projects).
 * Matches the look of the tenant roadmap Visual Timeline: section headers with colored dots, cards for items.
 */
/** Empty-state placeholder when a section has no content. */
const EmptySectionPlaceholder: React.FC<{ label: string }> = ({ label }) => {
  const { t } = useLanguage();
  return (
    <div className="rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-surface-bg)]/50 p-3">
      <span className="text-[var(--color-surface-text-secondary)] text-base italic">{label}: {t('planning.empty')}</span>
    </div>
  );
};

/** References block shown at bottom of preview when draft has sources. */
const DraftReferencesBlock: React.FC<{ draft: DraftSnapshot | null }> = ({ draft }) => {
  const { t } = useLanguage();
  const refs = draft?.references;
  if (!refs || refs.length === 0) return null;
  return (
    <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
      <h4 className="text-sm font-semibold text-[var(--color-surface-text-secondary)] mb-2">{t('planning.sources')}</h4>
      <ol className="text-sm text-[var(--color-surface-text)] space-y-1 list-decimal list-inside">
        {refs.map((r) => (
          <li key={r.id}>
            <span className="font-medium">{r.source}</span>
            {r.description && <span className="text-[var(--color-surface-text-secondary)]"> — {r.description}</span>}
          </li>
        ))}
      </ol>
    </div>
  );
};

const PlanningDraftVisualView: React.FC<{ draft: DraftSnapshot | null; ganttChartRef?: React.RefObject<HTMLDivElement> }> = ({ draft, ganttChartRef }) => {
  const { t } = useLanguage();
  if (!draft) {
    return (
      <div className="p-4 text-base text-[var(--color-surface-text-secondary)]">
        {t('planning.noRoadmapYet')}
      </div>
    );
  }

  const visionObj = draft.vision && typeof draft.vision === 'object' ? (draft.vision as Record<string, unknown>) : null;
  const visionLabel = visionObj ? nodeTitle(visionObj) : null;
  const visionDesc = visionObj ? ((visionObj['description'] ?? visionObj['summary']) as string) : null;
  const visionStatus = (visionObj?.['_status'] as string) || '';
  const roadmapStakeholders = visionObj ? toStakeholders(visionObj['stakeholders']) : [];
  const objectivesRaw = toArray(draft.objectives as Record<string, unknown> | null);
  const hasNested = objectivesRaw.length > 0 && typeof objectivesRaw[0]?.strategies !== 'undefined';
  // Flat draft.projects after bake mirrors nested projects — only show true orphans as "unassigned"
  const rootProjects = orphanRootProjects(draft);

  return (
    <div className="p-3 text-base text-[var(--color-surface-text)] font-sans space-y-4">
      {visionLabel ? (
        <div
          className="rounded-lg border p-4"
          style={{
            borderColor: visionStatus === 'new' ? '#2563EB' : visionStatus === 'changed' ? '#B45309' : visionStatus === 'existing' ? '#4B5563' : 'var(--color-border)',
            backgroundColor: visionStatus === 'new' ? 'rgba(37, 99, 235, 0.08)' : visionStatus === 'existing' ? 'rgba(107, 114, 128, 0.08)' : visionStatus === 'changed' ? 'rgba(217, 119, 6, 0.08)' : 'var(--color-secondary-bg)',
          }}
        >
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: visionStatus === 'new' ? '#2563EB' : visionStatus === 'changed' ? '#D97706' : visionStatus === 'existing' ? '#6B7280' : 'var(--color-info)' }}
            />
            <h3 className="font-semibold text-[var(--color-surface-text)]">{visionLabel}</h3>
            {visionStatus && <StatusBadge status={visionStatus} />}
          </div>
          {visionDesc && (
            <p className="mt-2 ml-5 text-[var(--color-surface-text-secondary)] text-sm">{visionDesc}</p>
          )}
          {objectivesRaw.length > 0 && (
            <div className="mt-3 ml-5">
              <div className="text-sm font-medium text-[var(--color-surface-text-secondary)] mb-1">{t('planning.objectivesWithTargets')}</div>
              <ol className="text-sm text-[var(--color-surface-text)] space-y-1 list-decimal list-inside">
                {objectivesRaw.map((obj, i) => (
                  <li key={i}>{objectiveLabel(obj)}</li>
                ))}
              </ol>
            </div>
          )}
          {roadmapStakeholders.length > 0 && (
            <div className="mt-3 ml-5">
              <div className="text-sm font-medium text-[var(--color-surface-text-secondary)] mb-1">{t('planning.roadmapStakeholders')}</div>
              <ul className="text-sm text-[var(--color-surface-text)] space-y-0.5">
                {roadmapStakeholders.map((s, i) => (
                  <li key={i}>{stakeholderLabel(s)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <EmptySectionPlaceholder label={t('planning.vision')} />
      )}

      {objectivesRaw.length === 0 && <EmptySectionPlaceholder label={t('planning.objectives')} />}

      {(() => {
        const { projects: timelineProjects, totalDuration } = mapDraftToTimelineProjects(draft);
        const hasStatus = timelineProjects.some((p) => (p as { _draftStatus?: string })._draftStatus);
        if (timelineProjects.length === 0) return <EmptySectionPlaceholder label={t('planning.timelineProjects')} />;
        return (
          <div ref={ganttChartRef} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] overflow-hidden">
            <div className="flex items-center justify-between px-4 pt-3 pb-1">
              <h4 className="font-semibold text-[var(--color-surface-text)]">{t('planning.timelineGantt')}</h4>
              {hasStatus && (
                <div className="flex items-center gap-3 text-sm text-[var(--color-surface-text-secondary)]">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#2563EB]" /> {t('planning.new')}</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#B45309]" /> {t('planning.changed')}</span>
                </div>
              )}
            </div>
            <VisualRoadmapTimeline
              projects={timelineProjects}
              roadmapName="Planning draft"
              totalDuration={totalDuration}
              isEditable={false}
            />
          </div>
        );
      })()}

      {hasNested ? (
        objectivesRaw.map((obj, oi) => {
          const strategies = toArray(obj.strategies as Record<string, unknown>);
          const color = getSectionColor(oi);
          const objStatus = (obj['_status'] as string) || '';
          const objBorder = objStatus === 'new' ? '#2563EB' : objStatus === 'changed' ? '#B45309' : objStatus === 'existing' ? '#4B5563' : undefined;
          return (
            <div
              key={oi}
              className="rounded-lg border bg-[var(--color-surface-bg)] p-4"
              style={{ borderColor: objBorder ?? 'var(--color-border)' }}
            >
              <div className="flex items-center gap-2 mb-3">
                <div
                  className="w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: objStatus === 'new' ? '#2563EB' : objStatus === 'changed' ? '#D97706' : objStatus === 'existing' ? '#6B7280' : color.primary }}
                />
                <h4 className="font-semibold text-[var(--color-surface-text)]">{objectiveLabel(obj)}</h4>
                {objStatus && <StatusBadge status={objStatus} />}
              </div>
              <div className="space-y-3 ml-5">
                {strategies.map((strat, si) => {
                  const projects = toArray(strat.projects as Record<string, unknown>);
                  const stratStatus = (strat['_status'] as string) || '';
                  const stratBg = stratStatus === 'new' ? '#DBEAFE' : stratStatus === 'changed' ? '#FEF3C7' : stratStatus === 'existing' ? '#E5E7EB' : color.secondary;
                  const stratColor = stratStatus === 'new' ? '#1D4ED8' : stratStatus === 'changed' ? '#B45309' : stratStatus === 'existing' ? '#4B5563' : color.border;
                  return (
                    <div key={si} className="space-y-2">
                      <div
                        className="text-sm font-medium rounded py-1 px-2 w-fit inline-flex items-center gap-1.5"
                        style={{ backgroundColor: stratBg, color: stratColor }}
                      >
                        <span className="opacity-70 font-normal">{t('planning.strategies')}:</span>
                        {nodeTitle(strat)}
                        {stratStatus && <StatusBadge status={stratStatus} />}
                      </div>
                      {projects.length > 0 && (
                        <div className="flex flex-col gap-2">
                          {projects.map((proj, pi) => {
                            const status = (proj['_status'] as string) || '';
                            const statusBorder = status === 'existing' ? '#4B5563' : status === 'new' ? '#2563EB' : status === 'changed' ? '#B45309' : color.border;
                            return <ProjectCard key={pi} proj={proj} borderColor={statusBorder} />;
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      ) : (
        <>
          {objectivesRaw.map((o, i) => {
            const color = getSectionColor(i);
            return (
              <div key={i} className="rounded-lg border border-[var(--color-border)] p-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: color.primary }} />
                  <span className="font-medium text-[var(--color-surface-text)]">{objectiveLabel(o)}</span>
                </div>
              </div>
            );
          })}
          {toArray(draft.strategies as Record<string, unknown>).map((s, i) => (
            <div key={i} className="rounded-lg border border-[var(--color-border)] p-2 ml-3">
              <span className="text-[var(--color-surface-text-secondary)]">{nodeTitle(s)}</span>
            </div>
          ))}
          <div className="ml-3 flex flex-col gap-2">
            {toArray(draft.projects as Record<string, unknown>).map((p, i) => {
              const status = (p['_status'] as string) || '';
              const statusBorder = status === 'existing' ? '#4B5563' : status === 'new' ? '#2563EB' : status === 'changed' ? '#B45309' : 'var(--color-border)';
              return <ProjectCard key={i} proj={p} borderColor={statusBorder} />;
            })}
          </div>
        </>
      )}

      {rootProjects.length > 0 && (
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 rounded-full bg-[var(--color-surface-text-secondary)] shrink-0" />
            <h4 className="font-semibold text-[var(--color-surface-text)]">{t('planning.unassignedProjects')}</h4>
          </div>
          <div className="flex flex-col gap-2 ml-5">
            {rootProjects.map((p, i) => {
              const status = (p['_status'] as string) || '';
              const statusBorder = status === 'existing' ? '#4B5563' : status === 'new' ? '#2563EB' : status === 'changed' ? '#B45309' : 'var(--color-border)';
              return <ProjectCard key={i} proj={p} borderColor={statusBorder} />;
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/** Check if draft has any _status (existing/new/changed) on vision, objectives, strategies, or projects. */
function draftHasAnyStatus(draft: DraftSnapshot | null): boolean {
  if (!draft) return false;
  const visionObj = draft.vision && typeof draft.vision === 'object' ? (draft.vision as Record<string, unknown>) : null;
  const visionStatus = (visionObj?.['_status'] as string) || '';
  if (visionStatus && ['existing', 'new', 'changed'].includes(visionStatus)) return true;
  const objectivesRaw = toArray(draft.objectives as Record<string, unknown> | null);
  const rootProjects = toArray(draft.projects as Record<string, unknown> | null);
  for (const p of rootProjects) {
    if ((p['_status'] as string) && ['existing', 'new', 'changed'].includes(p['_status'] as string)) return true;
  }
  for (const obj of objectivesRaw) {
    const os = (obj['_status'] as string) || '';
    if (['existing', 'new', 'changed'].includes(os)) return true;
    for (const strat of toArray(obj.strategies as Record<string, unknown>)) {
      const ss = (strat['_status'] as string) || '';
      if (['existing', 'new', 'changed'].includes(ss)) return true;
      for (const proj of toArray(strat.projects as Record<string, unknown>)) {
        if ((proj['_status'] as string) && ['existing', 'new', 'changed'].includes(proj['_status'] as string)) return true;
      }
    }
  }
  return false;
}

/** Recursively strip _status from draft so Summary of changes disappears after Apply. */
function stripStatusFromDraft(d: DraftSnapshot | null): DraftSnapshot | null {
  if (!d) return null;
  const strip = (o: Record<string, unknown> | unknown[] | null | undefined): Record<string, unknown> | unknown[] | null | undefined => {
    if (o == null) return o;
    if (Array.isArray(o)) return o.map((item) => (item != null && typeof item === 'object' ? strip(item as Record<string, unknown>) : item));
    const out = { ...(o as Record<string, unknown>) };
    delete out['_status'];
    for (const k of Object.keys(out)) {
      const v = out[k];
      if (v != null && typeof v === 'object' && !(v instanceof Date)) {
        out[k] = strip(v as Record<string, unknown>);
      }
    }
    return out;
  };
  return {
    vision: strip(d.vision as Record<string, unknown>) as Record<string, unknown> | null,
    objectives: strip(d.objectives as Record<string, unknown>) as Record<string, unknown> | null,
    strategies: strip(d.strategies as Record<string, unknown>) as Record<string, unknown> | null,
    projects: strip(d.projects as Record<string, unknown>) as Record<string, unknown> | null,
    references: d.references,
  };
}

/** Structure tree: vision → objectives → strategies → projects. Optional collapsible via expanded/onToggle. */
const StructureTreeBlock: React.FC<{
  draft: DraftSnapshot | null;
  showStatus?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
}> = ({ draft, showStatus = false, expanded = true, onToggle }) => {
  const { t } = useLanguage();
  if (!draft) return null;
  const visionObj = draft.vision && typeof draft.vision === 'object' ? (draft.vision as Record<string, unknown>) : null;
  const visionLabel = visionObj ? nodeTitle(visionObj) : t('planning.visionEmpty');
  const visionStatus = showStatus ? ((visionObj?.['_status'] as string) || '') : '';
  const objectivesRaw = toArray(draft.objectives as Record<string, unknown> | null);
  const hasNested = objectivesRaw.length > 0 && typeof objectivesRaw[0]?.strategies !== 'undefined';
  const rootProjects = orphanRootProjects(draft);
  const flatStrategies = toArray(draft.strategies as Record<string, unknown>);
  const hasAny = visionObj || objectivesRaw.length > 0 || rootProjects.length > 0 || flatStrategies.length > 0;
  if (!hasAny) return null;

  const isCollapsible = onToggle !== undefined;
  const isExpanded = isCollapsible ? expanded : true;

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] flex-shrink-0 overflow-hidden">
      <div
        className={`flex items-center gap-2 p-4 ${isCollapsible ? 'cursor-pointer hover:bg-[var(--color-secondary-bg)]' : ''}`}
        onClick={isCollapsible ? onToggle : undefined}
        onKeyDown={isCollapsible ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } } : undefined}
        role={isCollapsible ? 'button' : undefined}
        tabIndex={isCollapsible ? 0 : undefined}
        aria-expanded={isCollapsible ? isExpanded : undefined}
      >
        {isCollapsible && (
          <span className="flex-shrink-0 text-[var(--color-surface-text-secondary)]" aria-hidden>
            {isExpanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <h4 className="font-semibold text-[var(--color-surface-text)]">{t('planning.structure')}</h4>
          <p className="text-sm text-[var(--color-surface-text-secondary)]">{t('planning.structureDesc')}</p>
        </div>
      </div>
      {isExpanded && (
        <div className="px-4 pb-4 pt-0">
          {showStatus && (
            <div className="flex flex-wrap gap-3 mb-2 text-sm text-[var(--color-surface-text-secondary)]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#2563EB]" /> {t('planning.new')}</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#B45309]" /> {t('planning.changed')}</span>
            </div>
          )}
          <div className="space-y-0 font-mono text-sm text-[var(--color-surface-text)]">
            <TreeRow depth={0} label={visionLabel} variant="vision" status={visionStatus} />
            {hasNested
              ? objectivesRaw.map((obj, oi) => {
                  const strategies = toArray(obj.strategies as Record<string, unknown>);
                  const objStatus = showStatus ? ((obj['_status'] as string) || '') : '';
                  return (
                    <div key={oi}>
                      <TreeRow depth={1} label={objectiveLabel(obj)} variant="objective" status={objStatus} />
                      {strategies.map((strat, si) => {
                        const projects = toArray(strat.projects as Record<string, unknown>);
                        const stratStatus = showStatus ? ((strat['_status'] as string) || '') : '';
                        return (
                          <div key={si}>
                            <TreeRow depth={2} label={nodeTitle(strat)} variant="strategy" status={stratStatus} />
                            {projects.map((proj, pi) => (
                              <TreeRow key={pi} depth={3} label={nodeTitle(proj)} variant="project" status={showStatus ? ((proj['_status'] as string) || '') : ''} />
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              : objectivesRaw.map((o, i) => (
                  <TreeRow key={i} depth={1} label={objectiveLabel(o)} variant="objective" status={showStatus ? ((o['_status'] as string) || '') : ''} />
                ))}
            {!hasNested && flatStrategies.map((s, i) => (
              <TreeRow key={i} depth={2} label={nodeTitle(s)} variant="strategy" status={showStatus ? ((s['_status'] as string) || '') : ''} />
            ))}
            {rootProjects.map((p, i) => (
              <TreeRow key={i} depth={hasNested ? 1 : 2} label={nodeTitle(p)} variant="project" status={showStatus ? ((p['_status'] as string) || '') : ''} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/** Status badge for tree summary. Only show for "new" and "changed" — no badge for "existing". */
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useLanguage();
  if (!status || !['new', 'changed'].includes(status)) return null;
  const colors: Record<string, string> = {
    new: 'bg-[#2563EB] text-white',
    changed: 'bg-[#D97706] text-white',
  };
  const labels: Record<string, string> = {
    new: t('planning.new'),
    changed: t('planning.changed'),
  };
  return (
    <span className={`ml-1.5 px-2 py-0.5 rounded text-sm font-medium ${colors[status]}`}>
      {labels[status]}
    </span>
  );
};

/** Single tree row with indent, optional connector, and status badge */
const TreeRow: React.FC<{
  depth: number;
  isLast?: boolean;
  label: string;
  variant: 'vision' | 'objective' | 'strategy' | 'project';
  status?: string;
}> = ({ depth, isLast, label, variant, status }) => {
  const indent = depth * 16;
  const connector = depth === 0 ? null : (isLast ? TREE.last : TREE.branch) + TREE.dash + ' ';
  const variantClass = {
    vision: 'font-semibold text-[var(--color-surface-text)]',
    objective: 'text-[var(--color-info)]',
    strategy: 'text-[var(--color-success)]',
    project: 'text-[var(--color-surface-text-secondary)]',
  }[variant];
  const statusColor = status === 'new' ? 'text-[#2563EB]' : status === 'changed' ? 'text-[#D97706]' : '';

  return (
    <div
      className={`flex items-baseline gap-1 py-0.5 ${variantClass} ${statusColor}`}
      style={{ paddingLeft: indent }}
    >
      {connector && <span className="text-[var(--color-border)] select-none">{connector}</span>}
      <span>{label}</span>
      {status && <StatusBadge status={status} />}
    </div>
  );
};

interface PlanningChatPageProps {
  /** When true, use compact min-height so the section can start smaller (e.g. on landing page) and grow as needed. */
  embedded?: boolean;
  /** Authenticated /tenant/planning — fill the page; no nested card chrome. */
  workspace?: boolean;
}

const PlanningChatPage: React.FC<PlanningChatPageProps> = ({ embedded = false, workspace = false }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user, setUser } = useAuth();

  /** Keep profile photo in sync with nav (auth user may load without image, then refresh). */
  useEffect(() => {
    if (!isAuthenticated || !user?.id || !setUser) return;
    if (getUserProfilePictureUrl(user)) return;
    let cancelled = false;
    void (async () => {
      try {
        const userData = await authService.getCurrentUser();
        if (!cancelled && userData) setUser(userData);
      } catch {
        /* non-critical */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id, user?.profileImageUrl, setUser]);
  const urlSessionId = searchParams.get('sessionId');

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionName, setSessionName] = useState<string | null>(null);
  const [, setSessionCreatedAt] = useState<string | null>(null);
  const [, setSessionUpdatedAt] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState<DraftSnapshot | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [recentSessions, setRecentSessions] = useState<PlanningSessionSummary[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [editingListSessionId, setEditingListSessionId] = useState<string | null>(null);
  const [editListNameValue, setEditListNameValue] = useState('');
  const [editingHeaderName, setEditingHeaderName] = useState(false);
  const [editHeaderNameValue, setEditHeaderNameValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saveGatingOpen, setSaveGatingOpen] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [savedRoadmapId, setSavedRoadmapId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showApplyButton, setShowApplyButton] = useState(false);
  const [collabMode, setCollabMode] = useState<'discuss' | 'bake'>('discuss');
  const [collabStage, setCollabStage] = useState<'vision' | 'objectives' | 'strategies' | 'projects'>('vision');
  const [collabDraftVersion, setCollabDraftVersion] = useState(0);
  const [collabLayerState, setCollabLayerState] = useState<Record<
    string,
    { locked?: boolean; stale?: boolean }
  > | null>(null);
  const [collabMyRole, setCollabMyRole] = useState<string | null>(null);
  const [structureExpanded, setStructureExpanded] = useState(true);

  const applyCollabFromSession = useCallback((session: {
    mode?: string | null;
    currentStage?: string | null;
    currentStep?: string;
    draftVersion?: number | null;
    layerState?: Record<string, { locked?: boolean; stale?: boolean }> | null;
    myRole?: string | null;
  }) => {
    if (session.mode === 'discuss' || session.mode === 'bake') setCollabMode(session.mode);
    const stage = session.currentStage ?? session.currentStep;
    if (stage === 'vision' || stage === 'objectives' || stage === 'strategies' || stage === 'projects') {
      setCollabStage(stage);
    }
    if (session.draftVersion != null) setCollabDraftVersion(session.draftVersion);
    if (session.layerState) setCollabLayerState(session.layerState);
    if (session.myRole !== undefined) setCollabMyRole(session.myRole ?? null);
  }, []);

  /** Soft poll so co-viewers see draft/version and new chat messages. */
  useEffect(() => {
    if (!sessionId) return;
    const id = window.setInterval(() => {
      void (async () => {
        try {
          const res = await getPlanningCollabState(sessionId);
          if (!res.success) return;
          if (res.draftVersion != null && res.draftVersion !== collabDraftVersion) {
            if (res.draft !== undefined) setDraft(res.draft ?? null);
            setCollabDraftVersion(res.draftVersion);
            if (res.layerState) setCollabLayerState(res.layerState);
            if (res.mode === 'discuss' || res.mode === 'bake') setCollabMode(res.mode);
            if (
              res.currentStage === 'vision' ||
              res.currentStage === 'objectives' ||
              res.currentStage === 'strategies' ||
              res.currentStage === 'projects'
            ) {
              setCollabStage(res.currentStage);
            }
            if (res.myRole !== undefined) setCollabMyRole(res.myRole ?? null);
          }
          // Another participant posted — refresh transcript (skip while we are streaming a reply)
          if (
            typeof res.messageCount === 'number' &&
            res.messageCount > messages.length &&
            !loading
          ) {
            const loaded = await loadSession(sessionId);
            if (loaded.success && loaded.messages) {
              shouldScrollToBottomRef.current = true;
              setMessages(loaded.messages.map(mapApiMessageToChat));
              if (loaded.draft !== undefined) setDraft(loaded.draft ?? null);
              if (loaded.session) applyCollabFromSession(loaded.session);
            }
          }
        } catch {
          /* ignore poll errors */
        }
      })();
    }, 8000);
    return () => window.clearInterval(id);
  }, [sessionId, collabDraftVersion, messages.length, loading, applyCollabFromSession]);
  const [, setIsFrozen] = useState(false);
  const [attachedDocs, setAttachedDocs] = useState<{ id: string; fileName: string }[]>([]);
  const [attaching, setAttaching] = useState(false);
  const [attachingFileName, setAttachingFileName] = useState<string | null>(null);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [deleteConfirmSessionId, setDeleteConfirmSessionId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  type MergedModel = { id: string; displayName: string; modelName: string; source: string; sourceKey: string; usageExhausted: boolean; isActive: boolean };
  const [mergedModels, setMergedModels] = useState<MergedModel[]>([]);
  const [planningUsage, setPlanningUsage] = useState<PlanningUsageSummary | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [settingDefaultModel, setSettingDefaultModel] = useState(false);
  /** True when user has their own paid quota (active/trialing subscription); model selector is hidden otherwise. */
  const [hasOwnQuota, setHasOwnQuota] = useState(false);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const shouldScrollToBottomRef = useRef(false);
  const rightPanelScrollRef = useRef<HTMLDivElement>(null);
  const ganttChartRef = useRef<HTMLDivElement>(null);
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  const [viewMode, setViewMode] = useState<'chat' | 'preview'>('chat');
  const composerTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [composerFocused, setComposerFocused] = useState(false);
  const [planningHintIndex, setPlanningHintIndex] = useState(0);

  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
  const lastContent = (lastAssistant?.content ?? '').toLowerCase();
  const asksForConfirmation =
    /\b(should i|shall i|want me to|ready to|would you like me to)\s+(apply|bake|build|finalize|put (it|this) in|add (it|this)|update the roadmap)/i.test(lastContent) ||
    /(apply|bake)\s*(it\s*)?\?|click\s+apply|apply\s+below/i.test(lastContent);
  const showApplySection = showApplyButton || (asksForConfirmation && !loading);

  /** Example prompts only before the conversation starts (empty thread). */
  const showPlanningInputHints =
    viewMode === 'chat' &&
    messages.length === 0 &&
    !inputValue.trim() &&
    !loading &&
    !composerFocused &&
    !showApplySection &&
    attachedDocs.length === 0;

  /** Cycle example prompts: quick move-in, hold 2s, then next (see PLANNING_HINT_STEP_MS). */
  useEffect(() => {
    if (!showPlanningInputHints) {
      setPlanningHintIndex(0);
      return;
    }
    setPlanningHintIndex(0);
    const id = window.setInterval(() => {
      setPlanningHintIndex((i) => (i + 1) % PLANNING_INPUT_EXAMPLE_PROMPTS.length);
    }, PLANNING_HINT_STEP_MS);
    return () => clearInterval(id);
  }, [showPlanningInputHints]);

  /** Embedded landing: center composer until user focuses or types. Then dock to bottom so the window does not scroll to the focused field. */
  const embedCenterComposer =
    embedded &&
    messages.length === 0 &&
    !loading &&
    !error &&
    !composerFocused &&
    !inputValue.trim();

  /** Right panel: scroll to top when draft refreshes */
  useEffect(() => {
    rightPanelScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [draft]);

  /** New/session switch: reset structure expanded state */
  useEffect(() => {
    setStructureExpanded(true);
  }, [sessionId]);

  /** Close export dropdown when clicking outside */
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(e.target as Node)) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);
  const attachInputRef = useRef<HTMLInputElement>(null);
  /** When true, we received draft from POST—don't let getDraft effect overwrite with stale data. */
  const draftFromPostRef = useRef(false);
  const isLoggedIn = !!localStorage.getItem('auth_token');
  const showPicker = isLoggedIn && sessionId === null && !creating && !urlSessionId;
  /** Authenticated /tenant/planning — page shell is PlanningPage; no nested headings/cards. */
  const isWorkspace = workspace && !embedded;

  /** Defer models/billing fetch until user interacts (e.g. focuses chat input). Set by onFocus on the textarea. */
  const [shouldFetchModels, setShouldFetchModels] = useState(false);

  const refreshPlanningUsage = useCallback(async () => {
    const r = await fetchPlanningUsageSummary();
    if (r.success) setPlanningUsage(r);
    else setPlanningUsage(null);
  }, []);

  /** Token usage bar: all users (anonymous + logged-in). Refresh on load and after sends. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await fetchPlanningUsageSummary();
      if (!cancelled && r.success) setPlanningUsage(r);
    })();
    return () => { cancelled = true; };
  }, [isLoggedIn, urlSessionId]);

  /** Load merged models, effective default, and own-quota flag when logged in and user has interacted (focus input). */
  useEffect(() => {
    if (!isLoggedIn || !shouldFetchModels) return;
    let cancelled = false;
    (async () => {
      try {
        const [mergedRes, defaultRes, subsRes] = await Promise.all([
          api.get<{ models: MergedModel[] }>('ai/models/merged'),
          api.get<{ defaultModel: { id: string; displayName: string; modelName: string } | null }>('ai/models/effective-default', { params: { capability: 'strap' } }),
          api.get<{ subscriptions: { status: string }[] }>('ai/billing/tenant/subscriptions', { params: { scope: 'self' } }).catch(() => ({ data: { subscriptions: [] } })),
        ]);
        if (cancelled) return;
        const list = mergedRes.data?.models ?? [];
        setMergedModels(list);
        const defaultId = defaultRes.data?.defaultModel?.id;
        if (defaultId && list.some((m) => m.id === defaultId && !m.usageExhausted)) setSelectedModelId(defaultId);
        else if (list.length > 0 && !list[0].usageExhausted) setSelectedModelId(list[0].id);
        else setSelectedModelId('');
        const subs = subsRes.data?.subscriptions ?? [];
        const ownQuota = subs.some((s) => s.status === 'active' || s.status === 'trialing');
        setHasOwnQuota(ownQuota);
        void refreshPlanningUsage();
      } catch {
        if (!cancelled) setMergedModels([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isLoggedIn, shouldFetchModels, refreshPlanningUsage]);

  const handleSetDefaultModel = async (newModelId: string) => {
    setSelectedModelId(newModelId);
    if (!newModelId || settingDefaultModel) return;
    setSettingDefaultModel(true);
    try {
      await api.put('ai/models/default', { capability: 'strap', modelConfigurationId: newModelId });
    } finally {
      setSettingDefaultModel(false);
    }
  };

  /** Fetch draft when session changes (e.g. open/switch conversation). Do NOT overwrite when we've
   * received draft from POST (which is fresher); a late getDraft would race and overwrite with stale). */
  useEffect(() => {
    if (!sessionId) return;
    draftFromPostRef.current = false;
    let cancelled = false;
    (async () => {
      try {
        const res = await getDraft(sessionId);
        if (!cancelled && !draftFromPostRef.current) {
          if (res.success && res.draft) setDraft(res.draft);
          else setDraft(null);
        }
      } catch {
        if (!cancelled && !draftFromPostRef.current) setDraft(null);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  /** Scroll only the messages pane — never scroll the outer page. */
  const jumpChatToBottom = useCallback(() => {
    const run = () => {
      const scroller = messagesScrollRef.current;
      if (scroller) {
        scroller.scrollTop = scroller.scrollHeight;
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
      }
    };
    run();
    // Pane height / message layout may settle after first paint (split view, long threads).
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
  }, []);

  /** Opening a session or returning to chat should land on the latest message. */
  useEffect(() => {
    shouldScrollToBottomRef.current = true;
  }, [sessionId]);

  useEffect(() => {
    setEditingHeaderName(false);
    setEditHeaderNameValue('');
  }, [sessionId]);

  /** Follow the latest message when sending, streaming, or after loading a thread. */
  useEffect(() => {
    if (messages.length === 0) return;
    if (shouldScrollToBottomRef.current || loading) {
      jumpChatToBottom();
    }
    if (!loading) {
      shouldScrollToBottomRef.current = false;
    }
  }, [messages, loading, jumpChatToBottom]);

  /** One more jump after load completes so scrollHeight is final once the pane is laid out. */
  useEffect(() => {
    if (loading || messages.length === 0 || viewMode !== 'chat') return;
    jumpChatToBottom();
    const t = window.setTimeout(jumpChatToBottom, 120);
    return () => window.clearTimeout(t);
  }, [loading, sessionId, viewMode, jumpChatToBottom]);

  const prevViewModeRef = useRef(viewMode);
  /** Returning from roadmap preview → land at the bottom of the chat (once). */
  useEffect(() => {
    const prev = prevViewModeRef.current;
    prevViewModeRef.current = viewMode;
    if (viewMode === 'chat' && prev === 'preview') {
      shouldScrollToBottomRef.current = true;
      jumpChatToBottom();
    }
  }, [viewMode, jumpChatToBottom]);

  /** When URL has ?sessionId= and user is logged in, load that session (e.g. from conversation history). */
  useEffect(() => {
    if (!urlSessionId || !isLoggedIn) return;
    let cancelled = false;
    setCreating(false);
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await loadSession(urlSessionId);
        if (cancelled) return;
        if (res.success && res.session && res.messages) {
          setSessionId(res.session.id);
          setSessionName(res.session.name ?? null);
          setSessionCreatedAt(res.session.createdAt ?? null);
          setSessionUpdatedAt(res.session.updatedAt ?? null);
          setIsFrozen(res.session.isFrozen ?? false);
          shouldScrollToBottomRef.current = true;
          setMessages(res.messages.map(mapApiMessageToChat));
          if (res.draft !== undefined) setDraft(res.draft ?? null);
          setShowApplyButton(false);
          applyCollabFromSession(res.session);
          setSearchParams((p) => {
            const next = new URLSearchParams(p);
            next.delete('sessionId');
            return next;
          }, { replace: true });
        } else {
          setError('Session not found or cannot be loaded');
        }
      } catch (e: unknown) {
        if (!cancelled) {
          const msg = e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { data?: { message?: string }; status?: number } }).response?.data?.message
            : (e instanceof Error ? e.message : 'Failed to load session');
          setError(msg || 'Failed to load session');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [urlSessionId, isLoggedIn, setSearchParams]);

  /** On mount: if no urlSessionId and logged in, fetch recent sessions (picker). Anonymous session is created on first send (deferred). */
  useEffect(() => {
    if (urlSessionId) return;
    if (!isLoggedIn) return;
    let cancelled = false;
    setError(null);
    setLoadingSessions(true);
    (async () => {
      try {
        const res = await listSessions();
        if (!cancelled && res.success) setRecentSessions(res.sessions || []);
      } catch {
        if (!cancelled) setRecentSessions([]);
      } finally {
        if (!cancelled) setLoadingSessions(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isLoggedIn, urlSessionId]);

  const handleSend = async (overrideMessage?: string) => {
    let text = (overrideMessage ?? inputValue).trim();
    if (loading) return;
    if (isLoggedIn && !sessionId) return;
    if (!text && attachedDocs.length === 0) return;

    let sid = sessionId;
    if (!sid && !isLoggedIn) {
      setCreating(true);
      setError(null);
      try {
        const res = await createSession(null, null);
        if (!res.success || !res.session) {
          setError('Failed to create session');
          setCreating(false);
          return;
        }
        sid = res.session.id;
        setSessionId(sid);
        setSessionName(res.session.name ?? null);
        setSessionCreatedAt(res.session.createdAt ?? null);
        setSessionUpdatedAt(res.session.updatedAt ?? null);
        setIsFrozen(res.session.isFrozen ?? false);
        applyCollabFromSession(res.session);
      } catch (e: unknown) {
        console.error('[PlanningChat] createSession failed:', e);
        const msg = getApiErrorMessage(e, 'Failed to create session');
        setError(msg || 'Failed to create session');
        setCreating(false);
        return;
      } finally {
        setCreating(false);
      }
    }
    if (!sid) return;
    if (!text && attachedDocs.length > 0) text = 'Please analyze the attached document(s) and help me build or refine a transformation roadmap.';
    const attachedDocIds = attachedDocs.map((d) => d.id);
    if (!overrideMessage) {
      setInputValue('');
      if (composerTextareaRef.current) {
        composerTextareaRef.current.style.height = 'auto';
      }
    }
    setAttachedDocs([]);
    setShowApplyButton(false);
    const displayName =
      user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || null : null;
    const userMsg: ChatMessage = {
      id: `u_${Date.now()}`,
      role: 'user',
      content: text,
      createdAt: new Date().toISOString(),
      userId: user?.id ?? null,
      userDisplayName: displayName,
      userAvatarUrl: getUserProfilePictureUrl(user),
    };
    const assistantId = `a_${Date.now()}`;
    const assistantMsg: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      createdAt: new Date().toISOString(),
    };
    shouldScrollToBottomRef.current = true;
    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setLoading(true);
    setError(null);
    try {
      await postMessageStream(
        sid,
        text,
        {
          attachedDocumentIds: attachedDocIds,
          modelConfigurationId: selectedModelId || undefined,
          onChunk: (chunkText: string) => {
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + chunkText } : m))
            );
          },
          onDone: (payload) => {
            if (isPlanningQuotaPayload(payload)) {
              let limitDbg: ChatMessage['limitDebug'] | undefined;
              const ld = payload.limitDebug;
              if (ld && 'cap' in ld && 'used' in ld) {
                limitDbg = { cap: ld.cap, used: ld.used };
              } else if (ld && 'periodQuota' in ld && 'periodUsed' in ld) {
                limitDbg = { cap: ld.periodQuota, used: ld.periodUsed };
              }
              setError(null);
              setMessages((prev) => {
                const withoutUserAndAssistant = prev.filter((m) => m.id !== userMsg.id && m.id !== assistantId);
                return [
                  ...withoutUserAndAssistant,
                  {
                    id: `quota-${Date.now()}`,
                    role: 'assistant' as const,
                    content: QUOTA_EXHAUSTED_MARKER,
                    createdAt: new Date().toISOString(),
                    limitDebug: limitDbg,
                    serverQuotaMessage: payload.code === 'QUOTA_EXCEEDED' ? payload.message : undefined,
                  },
                ];
              });
              void refreshPlanningUsage();
              return;
            }
            if (payload.success && payload.reply != null) {
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantId ? { ...m, content: payload.reply! } : m))
              );
              draftFromPostRef.current = true;
              const nextDraft = payload.draft ?? null;
              // Never wipe an existing preview when the payload omits draft (collab bake must still refresh when present).
              if (nextDraft) {
                setDraft(overrideMessage === 'apply it' ? stripStatusFromDraft(nextDraft) : nextDraft);
              } else if (payload.collaborative && payload.draftVersion != null) {
                // Collab claimed a version bump but forgot draft — reload from server
                void (async () => {
                  try {
                    const loaded = await loadSession(sid);
                    if (loaded.success && loaded.draft) setDraft(loaded.draft);
                  } catch {
                    /* ignore */
                  }
                })();
              }
              if (payload.sessionName !== undefined) setSessionName(payload.sessionName ?? null);
              if (payload.sessionUpdatedAt !== undefined)
                setSessionUpdatedAt(payload.sessionUpdatedAt ?? null);
              setShowApplyButton(payload.showApplyButton ?? false);
              if (payload.mode === 'discuss' || payload.mode === 'bake') setCollabMode(payload.mode);
              if (
                payload.currentStage === 'vision' ||
                payload.currentStage === 'objectives' ||
                payload.currentStage === 'strategies' ||
                payload.currentStage === 'projects'
              ) {
                setCollabStage(payload.currentStage);
              }
              if (payload.layerState) setCollabLayerState(payload.layerState);
              if (payload.draftVersion != null) setCollabDraftVersion(payload.draftVersion);
              void refreshPlanningUsage();
            } else {
              setError(payload.message || 'Failed to get reply');
              setMessages((prev) => prev.filter((m) => m.id !== assistantId));
            }
          },
        }
      );
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Failed to send message'));
      setMessages((prev) => prev.filter((m) => m.id !== assistantId));
    } finally {
      setLoading(false);
    }
  };

  const handleAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || attaching) return;
    if (isLoggedIn && !sessionId) return;

    let sid = sessionId;
    if (!sid && !isLoggedIn) {
      setCreating(true);
      setError(null);
      try {
        const createRes = await createSession(null, null);
        if (!createRes.success || !createRes.session) {
          setError('Failed to create session');
          setCreating(false);
          return;
        }
        sid = createRes.session.id;
        setSessionId(sid);
        setSessionName(createRes.session.name ?? null);
        setSessionCreatedAt(createRes.session.createdAt ?? null);
        setSessionUpdatedAt(createRes.session.updatedAt ?? null);
        setIsFrozen(createRes.session.isFrozen ?? false);
      } catch (err: unknown) {
        console.error('[PlanningChat] createSession failed (attach):', err);
        setError(getApiErrorMessage(err, 'Failed to create session'));
        setCreating(false);
        return;
      } finally {
        setCreating(false);
      }
    }
    if (!sid) return;

    setAttaching(true);
    setAttachingFileName(file.name);
    setError(null);
    try {
      const res = await attachDocument(sid, file);
      if (res.success && res.doc) {
        setAttachedDocs((prev) => [...prev, { id: res.doc!.id, fileName: res.doc!.fileName }]);
        // If JSON and looks like roadmap, also set as uploaded roadmap for preview (existing/new/changed)
        if (file.name.toLowerCase().endsWith('.json')) {
          try {
            const text = await file.text();
            const parsed = JSON.parse(text) as DraftSnapshot;
            if (parsed && typeof parsed === 'object' && (parsed.vision || parsed.objectives || parsed.projects)) {
              const upRes = await uploadRoadmap(sid, parsed);
              if (upRes.success && upRes.draft) {
                draftFromPostRef.current = true;
                setDraft(upRes.draft);
              }
            }
          } catch {
            // ignore – treat as plain document only
          }
        }
      } else {
        setError(res.message || 'Failed to attach file');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to attach file');
    } finally {
      setAttaching(false);
      setAttachingFileName(null);
    }
  };

  const handleSave = async () => {
    if (!sessionId) return;
    if (!isLoggedIn) {
      try {
        await flagPendingSave(sessionId);
      } catch {
        // ignore; still show sign-in so user can log in and save
      }
      setSaveGatingOpen(true);
      return;
    }
    setSaving(true);
    try {
      const res = await saveToWorkspace(sessionId);
      if (res.success) {
        setSavedRoadmapId(res.roadmapId ?? null);
        setSaveSuccess('Roadmap saved.');
        setTimeout(() => {
          setSaveSuccess(null);
          setSavedRoadmapId(null);
        }, 8000);
      } else {
        setError('Save failed');
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string }; status?: number } };
      if (err.response?.status === 401) {
        setSaveGatingOpen(true);
        return;
      }
      setError(err.response?.data?.message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleStartNewConversation = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await createSession(null, null);
      if (res.success && res.session) {
        setSessionId(res.session.id);
        setSessionName(res.session.name ?? null);
        setSessionCreatedAt(res.session.createdAt ?? null);
        setSessionUpdatedAt(res.session.updatedAt ?? null);
        setIsFrozen(res.session.isFrozen);
        setMessages([]);
        setDraft(null);
        setShowApplyButton(false);
        applyCollabFromSession(res.session);
      } else {
        setError('Failed to create session');
      }
    } catch (e: unknown) {
      console.error('[PlanningChat] createSession failed (new conversation):', e);
      setError(getApiErrorMessage(e, 'Failed to create session'));
    } finally {
      setCreating(false);
    }
  };

  const handleContinueConversation = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await loadSession(id);
      if (res.success && res.session && res.messages) {
        setSessionId(res.session.id);
        setSessionName(res.session.name ?? null);
        setSessionCreatedAt(res.session.createdAt ?? null);
        setSessionUpdatedAt(res.session.updatedAt ?? null);
        setIsFrozen(res.session.isFrozen ?? false);
        shouldScrollToBottomRef.current = true;
        setMessages(res.messages.map(mapApiMessageToChat));
        if (res.draft !== undefined) setDraft(res.draft ?? null);
        setShowApplyButton(false);
        applyCollabFromSession(res.session);
      } else {
        setError('Failed to load session');
      }
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'response' in e
        ? (e as { response?: { data?: { message?: string }; status?: number } }).response?.data?.message
        : (e instanceof Error ? e.message : 'Failed to load session');
      setError(msg || 'Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEditListName = useCallback((s: PlanningSessionSummary) => {
    setEditingListSessionId(s.id);
    setEditListNameValue(s.name || '');
  }, []);

  const handleSaveListName = useCallback(async () => {
    const id = editingListSessionId;
    if (!id) return;
    const trimmed = editListNameValue.trim().slice(0, 255);
    setEditingListSessionId(null);
    if (trimmed === (recentSessions.find((s) => s.id === id)?.name || '').trim()) return;
    try {
      await updateSessionName(id, trimmed);
      setRecentSessions((prev) => prev.map((s) => (s.id === id ? { ...s, name: trimmed || null } : s)));
      if (sessionId === id) setSessionName(trimmed || null);
    } catch {
      setEditingListSessionId(id);
    }
  }, [editingListSessionId, editListNameValue, recentSessions, sessionId]);

  const sessionDisplayName = (name: string | null | undefined) => name?.trim() || 'New conversation';

  const handleStartEditHeaderName = useCallback(() => {
    if (!sessionId || !isLoggedIn) return;
    setEditingHeaderName(true);
    setEditHeaderNameValue(sessionName?.trim() || '');
  }, [sessionId, isLoggedIn, sessionName]);

  const handleSaveHeaderName = useCallback(async () => {
    if (!sessionId) return;
    const trimmed = editHeaderNameValue.trim().slice(0, 255);
    setEditingHeaderName(false);
    if (trimmed === (sessionName || '').trim()) return;
    try {
      const res = await updateSessionName(sessionId, trimmed);
      const nextName = res.name !== undefined ? res.name : trimmed || null;
      setSessionName(nextName);
      setRecentSessions((prev) => prev.map((s) => (s.id === sessionId ? { ...s, name: nextName } : s)));
    } catch {
      setEditingHeaderName(true);
    }
  }, [sessionId, editHeaderNameValue, sessionName]);

  const handleCancelEditHeaderName = useCallback(() => {
    setEditingHeaderName(false);
    setEditHeaderNameValue('');
  }, []);

  const handleCancelEditListName = useCallback(() => {
    setEditingListSessionId(null);
    setEditListNameValue('');
  }, []);

  const handleDeleteConversation = async (id: string) => {
    setDeleteConfirmSessionId(id);
  };

  const handleConfirmDelete = async () => {
    const id = deleteConfirmSessionId;
    if (!id || deleting) return;
    setDeleting(true);
    try {
      const res = await deleteSession(id);
      if (res.success) {
        setDeleteConfirmSessionId(null);
        if (sessionId === id) {
          setSessionId(null);
          setSessionName(null);
          setMessages([]);
          setDraft(null);
          setShowApplyButton(false);
          const listRes = await listSessions();
          if (listRes.success) setRecentSessions(listRes.sessions || []);
        } else {
          setRecentSessions((prev) => prev.filter((s) => s.id !== id));
        }
      } else {
        setError(res.message || 'Failed to delete');
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string }; status?: number } };
      setError(err.response?.data?.message ?? (e instanceof Error ? e.message : 'Failed to delete'));
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = async (format: 'text' | 'pdf' | 'docx') => {
    if (!sessionId) return;
    if (!isLoggedIn) {
      setSaveGatingOpen(true);
      return;
    }
    try {
      if ((format === 'pdf' || format === 'docx') && draft) {
        // Same architecture as PDF: backend renders Gantt server-side (vector/table), no image capture
        await downloadSessionWithChart(sessionId, format, '');
      } else {
        await downloadSession(sessionId, format);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string }; status?: number } };
      if (err.response?.status === 401) {
        setSaveGatingOpen(true);
        return;
      }
      setError(e instanceof Error ? e.message : 'Download failed');
    }
  };

  if (creating && !isLoggedIn && !urlSessionId) {
    return (
      <div className={isWorkspace ? 'w-full' : 'w-full px-4 md:px-6 lg:px-8'}>
        <div className="p-8 text-center text-[var(--color-surface-text)]">
          Creating planning session…
        </div>
      </div>
    );
  }

  if (urlSessionId && loading && !sessionId) {
    return (
      <div className={isWorkspace ? 'w-full' : 'w-full px-4 md:px-6 lg:px-8'}>
        <div className="p-8 text-center text-[var(--color-surface-text)]">
          Loading conversation…
        </div>
      </div>
    );
  }

  /** Logged-in user: start new or continue a recent conversation. */
  if (showPicker) {
    const pickerBody = (
      <>
        <p
          className={
            isWorkspace
              ? 'text-sm text-[var(--color-secondary-text)] mb-4'
              : 'text-base text-[var(--color-surface-text-secondary)] mb-4'
          }
        >
          Start a new conversation or continue a recent one (conversations are kept per platform settings).
        </p>
        {loadingSessions ? (
          <p className="text-base text-[var(--color-surface-text-secondary)]">Loading recent conversations…</p>
        ) : (
          <>
            <button
              type="button"
              onClick={handleStartNewConversation}
              disabled={creating}
              className="mb-4 px-4 py-2 rounded-md bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:opacity-90 disabled:opacity-50 w-fit"
            >
              {creating ? 'Creating…' : 'New conversation'}
            </button>
            {recentSessions.length > 0 && (
              <div>
                <p className="text-base font-medium text-[var(--color-surface-text)] mb-2">Recent conversations</p>
                <ul className="space-y-1">
                  {recentSessions.map((s) => (
                    <li key={s.id} className="flex items-stretch gap-1">
                      <div className="flex-1 flex flex-col gap-0.5 min-w-0 rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] overflow-hidden">
                        {editingListSessionId === s.id ? (
                          <div className="flex items-center gap-2 px-3 py-2" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              value={editListNameValue}
                              onChange={(e) => setEditListNameValue(e.target.value)}
                              onBlur={handleSaveListName}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveListName();
                                if (e.key === 'Escape') handleCancelEditListName();
                              }}
                              autoFocus
                              className="flex-1 min-w-0 text-base font-medium text-[var(--color-surface-text)] bg-[var(--color-surface-bg)] border border-[var(--color-border)] rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary-bg)]"
                              placeholder="Conversation name"
                            />
                            <button
                              type="button"
                              onClick={handleSaveListName}
                              className="shrink-0 px-2 py-1 text-sm rounded bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:opacity-90"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={handleCancelEditListName}
                              className="shrink-0 px-2 py-1 text-sm rounded border border-[var(--color-border)] text-[var(--color-surface-text)] hover:bg-[var(--color-surface-hover)]"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-2 px-3 py-2 min-w-0">
                              <button
                                type="button"
                                onClick={() => handleContinueConversation(s.id)}
                                disabled={loading}
                                className="flex-1 min-w-0 text-left group"
                              >
                                <span className="font-medium block truncate text-[var(--color-surface-text)] group-hover:underline" title={s.name || undefined}>
                                  {s.name || 'New conversation'}
                                </span>
                                <span className="text-[var(--color-surface-text-secondary)] text-base block">
                                  {s.messageCount} message{s.messageCount !== 1 ? 's' : ''}
                                  {' · '}
                                  Created {formatRelativeDate(s.createdAt)}
                                  {s.createdAt !== s.updatedAt && (
                                    <> · Updated {formatRelativeDate(s.updatedAt)}</>
                                  )}
                                  {s.isFrozen && ' · Frozen'}
                                </span>
                              </button>
                              {isLoggedIn && (
                                <>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); handleStartEditListName(s); }}
                                    disabled={loading}
                                    title="Edit conversation name"
                                    className="shrink-0 p-1.5 rounded text-[var(--color-surface-text-secondary)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-surface-text)] disabled:opacity-50"
                                    aria-label="Edit conversation name"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteConversation(s.id); }}
                                    disabled={loading}
                                    title="Delete conversation"
                                    className="shrink-0 p-1.5 rounded text-[var(--color-surface-text-secondary)] hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                                    aria-label="Delete conversation"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
        {error && (
          <div className="mt-4 px-4 py-2 bg-red-100 text-red-800 text-base rounded">
            {error}
          </div>
        )}
      </>
    );

    if (isWorkspace) {
      return <div className="flex w-full flex-col flex-1 min-h-0">{pickerBody}</div>;
    }

    return (
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div
          className={`flex flex-col bg-[var(--color-surface-bg)] rounded-lg border border-[var(--color-border)] p-6 ${
            embedded ? 'min-h-[260px]' : 'h-[calc(100vh-12rem)] min-h-[400px]'
          }`}
        >
          {pickerBody}
        </div>
      </div>
    );
  }

  /* Anonymous with no session: fall through to main chat UI so user can type and we create session on first send. */

  /**
   * Desktop workspace: chat + Preview side-by-side.
   * Embedded landing / narrow screens: keep the chat↔Preview toggle.
   */
  const splitDesktop = isWorkspace || !embedded;

  const rootLayoutClass = embedded
    ? 'max-w-6xl h-full min-h-0 flex-1 bg-gray-100 px-4 md:px-6 lg:px-8'
    : isWorkspace
      ? 'h-full min-h-0 w-full'
      : 'max-w-[96rem] min-h-[calc(100vh-12rem)] px-4 md:px-6 lg:px-8';

  /** Workspace panes fill their column edge-to-edge (page shell already has container padding). */
  const panePadX = isWorkspace ? 'px-3' : 'px-4';

  const paneHeaderClass = embedded
    ? 'flex h-12 flex-shrink-0 flex-nowrap items-center justify-between gap-2 px-3 border-b border-gray-400/35 bg-transparent'
    : 'flex h-12 flex-shrink-0 flex-nowrap items-center justify-between gap-2 px-3 border-b border-[var(--color-border)] bg-[var(--color-surface-bg)]';

  const paneHeaderButtonClass = embedded
    ? 'inline-flex h-8 items-center gap-1 px-2.5 text-sm rounded-lg border border-gray-400/45 text-gray-900 bg-transparent hover:bg-gray-200/40 disabled:opacity-60 disabled:cursor-not-allowed'
    : 'inline-flex h-8 items-center gap-1 px-2.5 text-sm rounded-lg border border-[var(--color-border)] text-[var(--color-surface-text)] hover:bg-[var(--color-secondary-bg)] disabled:opacity-60 disabled:cursor-not-allowed';

  const paneTitleClass = embedded
    ? 'min-w-0 shrink truncate pr-2 text-lg font-semibold leading-none text-gray-900'
    : 'min-w-0 shrink truncate pr-2 text-lg font-semibold leading-none text-[var(--color-surface-text)]';

  const collabToolbarProps = sessionId
    ? {
        sessionId,
        mode: collabMode,
        currentStage: collabStage,
        draftVersion: collabDraftVersion,
        layerState: collabLayerState,
        myRole: collabMyRole,
        canCollaborate: Boolean(isLoggedIn && collabMyRole),
        onModeChange: setCollabMode,
        onStageChange: setCollabStage,
        onLayerStateChange: setCollabLayerState,
        onDraftChange: (d: DraftSnapshot | null, v: number) => {
          setDraft(d);
          setCollabDraftVersion(v);
        },
        onReloadMessages: () => {
          void (async () => {
            try {
              const res = await loadSession(sessionId);
              if (res.success && res.messages) {
                shouldScrollToBottomRef.current = true;
                setMessages(res.messages.map(mapApiMessageToChat));
              }
              if (res.draft !== undefined) setDraft(res.draft ?? null);
              if (res.session?.draftVersion != null) {
                setCollabDraftVersion(res.session.draftVersion);
              }
              if (res.session?.layerState) setCollabLayerState(res.session.layerState);
              if (res.session?.mode === 'discuss' || res.session?.mode === 'bake') {
                setCollabMode(res.session.mode);
              }
              const stage = res.session?.currentStage ?? res.session?.currentStep;
              if (
                stage === 'vision' ||
                stage === 'objectives' ||
                stage === 'strategies' ||
                stage === 'projects'
              ) {
                setCollabStage(stage);
              }
            } catch {
              /* ignore */
            }
          })();
        },
      }
    : null;

  return (
    <div
      className={`flex flex-col flex-1 min-h-0 h-full w-full overflow-hidden ${isWorkspace ? '' : 'mx-auto'} ${rootLayoutClass}`}
    >
      {/* Mobile / embedded: toggle between panes. Hidden on lg+ when split is on. */}
      <div
        className={`flex items-center justify-end gap-2 mb-2 px-1 flex-shrink-0 ${
          splitDesktop ? 'lg:hidden' : ''
        }`}
      >
        {viewMode === 'preview' && (
          <button
            type="button"
            onClick={() => setViewMode('chat')}
            className={
              embedded
                ? 'px-3 py-2 text-base rounded-lg border border-gray-400/45 text-gray-700 bg-transparent hover:bg-gray-200/40'
                : 'px-3 py-2 text-base rounded-lg border border-[var(--color-border)] text-[var(--color-surface-text)] hover:bg-[var(--color-secondary-bg)]'
            }
          >
            Back to chat
          </button>
        )}
        {viewMode === 'chat' && (
          <button
            type="button"
            onClick={() => setViewMode('preview')}
            disabled={!draft}
            className={
              embedded
                ? 'px-4 py-2 text-base font-medium rounded-lg border border-gray-400/50 text-gray-800 bg-transparent hover:bg-gray-200/45 disabled:opacity-50'
                : 'px-4 py-2.5 text-base font-medium rounded-lg bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] shadow-md hover:shadow-lg active:shadow disabled:opacity-50 disabled:shadow-none transition-shadow'
            }
          >
            Preview
          </button>
        )}
      </div>

      <div
        className={`flex flex-1 min-h-0 h-full w-full overflow-hidden flex-col gap-3 ${
          splitDesktop
            ? isWorkspace
              ? 'lg:grid lg:grid-cols-2 lg:gap-3'
              : 'lg:flex-row'
            : ''
        } ${isWorkspace ? 'mt-0' : 'mt-2'}`}
      >
        {/* Chat pane — full width of left column in workspace split */}
        <div
          className={`min-h-0 min-w-0 h-full max-h-full w-full flex-col ${
            viewMode === 'chat' ? 'flex' : 'hidden'
          } ${splitDesktop ? 'lg:flex lg:h-full' : 'flex-1'} ${
            isWorkspace || !embedded
              ? 'rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)]'
              : embedded
                ? 'min-h-[200px] rounded-xl border border-gray-400/45 bg-transparent'
                : ''
          }`}
        >
          {/* Chat header — session name (editable) + token usage + planning settings */}
          <div className={`relative z-30 shrink-0 ${paneHeaderClass}`}>
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
              {editingHeaderName && sessionId && isLoggedIn ? (
                <input
                  type="text"
                  value={editHeaderNameValue}
                  onChange={(e) => setEditHeaderNameValue(e.target.value)}
                  onBlur={handleSaveHeaderName}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveHeaderName();
                    if (e.key === 'Escape') handleCancelEditHeaderName();
                  }}
                  autoFocus
                  className={`${paneTitleClass} w-full max-w-[min(100%,20rem)] rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary-bg)]`}
                  placeholder="Conversation name"
                  aria-label="Conversation name"
                />
              ) : (
                <>
                  <h2
                    className={`${paneTitleClass}${isLoggedIn && sessionId ? ' cursor-pointer hover:underline' : ''}`}
                    title={sessionDisplayName(sessionName)}
                    onClick={() => {
                      if (isLoggedIn && sessionId) handleStartEditHeaderName();
                    }}
                  >
                    {sessionDisplayName(sessionName)}
                  </h2>
                  {isLoggedIn && sessionId && (
                    <button
                      type="button"
                      onClick={handleStartEditHeaderName}
                      className="shrink-0 rounded p-1 text-[var(--color-surface-text-secondary)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-surface-text)]"
                      aria-label="Edit conversation name"
                      title="Edit conversation name"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="flex shrink-0 items-center justify-end gap-2">
              {isLoggedIn && planningUsage && planningUsage.success && planningUsage.capTokens > 0 && (
                <div className="group relative">
                  <div className="inline-flex h-8 items-center rounded-full border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-2.5 text-[11px] font-medium text-[var(--color-surface-text)]">
                    {t('planning.tokenUsage')}
                  </div>
                  <div className="pointer-events-none absolute right-0 top-full z-40 mt-1 w-max max-w-[16rem] rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-2.5 py-1.5 text-xs text-[var(--color-surface-text)] opacity-0 shadow-md transition-opacity group-hover:opacity-100">
                    <div className="font-medium">
                      {planningUsage.kind === 'subscription'
                        ? t('planning.usageBarPeriod')
                        : t('planning.usageBarDaily')}
                    </div>
                    <div className="mt-0.5 tabular-nums">
                      {planningUsage.usedTokens.toLocaleString()} /{' '}
                      {planningUsage.capTokens.toLocaleString()} {t('planning.tokens')}
                    </div>
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-secondary-bg)]">
                      <div
                        className="h-full rounded-full bg-[var(--color-info)]"
                        style={{
                          width: `${Math.min(
                            100,
                            Math.max(0, (planningUsage.usedTokens / planningUsage.capTokens) * 100)
                          )}%`,
                        }}
                      />
                    </div>
                    {planningUsage.kind === 'subscription' && planningUsage.topUpBalanceUsd > 0 && (
                      <div className="mt-1 text-[var(--color-surface-text-secondary)]">
                        {t('planning.usageTopUpBalance', {
                          amount: planningUsage.topUpBalanceUsd.toFixed(2),
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {collabToolbarProps && <PlanningCollabToolbar {...collabToolbarProps} />}
            </div>
          </div>

          <div
            className={
              embedded
                ? 'relative flex flex-col flex-1 min-h-0 w-full overflow-hidden bg-transparent'
                : 'relative flex flex-col flex-1 min-h-0 w-full overflow-hidden rounded-b-lg bg-[var(--color-secondary-bg)]'
            }
          >
            {embedCenterComposer && <div className="min-h-0 w-full min-w-0 flex-1 shrink" aria-hidden />}
            {/* Messages scroll; composer stays docked below. */}
            <div
              ref={messagesScrollRef}
              className={`${panePadX} pt-2 pb-4 space-y-4 overflow-y-auto min-h-0 overscroll-y-contain w-full ${
                embedCenterComposer ? 'hidden' : 'flex-1'
              }`}
            >
              {messages.map((m) => {
                const isOwnUserMessage =
                  m.role === 'user' &&
                  Boolean(user?.id) &&
                  (m.userId == null || m.userId === user?.id);
                const avatarUrl =
                  m.role === 'user'
                    ? (isOwnUserMessage ? getUserProfilePictureUrl(user) : null) ||
                      resolveProfileImageUrl(m.userAvatarUrl) ||
                      null
                    : null;
                const displayName =
                  m.userDisplayName ||
                  (isOwnUserMessage && user
                    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email
                    : null);

                return (
                <div
                  key={m.id}
                  className={`flex items-start gap-2 w-full ${
                    m.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`${
                      isWorkspace && m.role === 'assistant' ? 'max-w-full w-full' : 'max-w-[85%]'
                    } ${m.role === 'assistant' && isWorkspace ? 'px-2 py-1' : 'px-4 py-2'} ${
                      m.role === 'user'
                        ? embedded
                          ? 'rounded-2xl bg-white text-gray-950 shadow-sm ring-1 ring-gray-200/80'
                          : 'rounded-[1.25rem] border-2 border-[var(--color-border)] bg-[var(--color-surface-bg)] shadow-sm text-[var(--color-surface-text)]'
                        : 'rounded-lg bg-transparent text-[var(--color-surface-text)]'
                    }`}
                  >
                    <div className="text-base whitespace-pre-wrap leading-relaxed">
                      {m.role === 'assistant'
                        ? isQuotaExhaustedMessage(m)
                          ? (() => (
                              <>
                                {m.serverQuotaMessage ? (
                                  <>
                                    <p className="text-[var(--color-surface-text)]">{m.serverQuotaMessage}</p>
                                    <p className="mt-2">
                                      {isAuthenticated ? (
                                        <Link to="/settings/profile?section=subscription" className="text-[var(--color-info)] underline font-medium hover:opacity-90">{t('planning.upgradePlan')}</Link>
                                      ) : (
                                        <Link to="/register" className="text-[var(--color-info)] underline font-medium hover:opacity-90">{t('planning.signUp')}</Link>
                                      )}
                                    </p>
                                  </>
                                ) : (
                                  <p>
                                    {t('planning.quotaExhausted')}{' '}
                                    {isAuthenticated ? (
                                      <Link to="/settings/profile?section=subscription" className="text-[var(--color-info)] underline font-medium hover:opacity-90">{t('planning.upgradePlan')}</Link>
                                    ) : (
                                      <Link to="/register" className="text-[var(--color-info)] underline font-medium hover:opacity-90">{t('planning.signUp')}</Link>
                                    )}
                                    .
                                  </p>
                                )}
                                {m.limitDebug && (
                                  <p className="mt-2 text-sm text-[var(--color-surface-text-secondary)] opacity-90">
                                    {t('planning.dailyLimit')}: <strong>{m.limitDebug.cap.toLocaleString()}</strong> {t('planning.tokens')} · {t('planning.used')}: <strong>{m.limitDebug.used.toLocaleString()}</strong> {t('planning.tokens')}
                                  </p>
                                )}
                              </>
                            ))()
                          : (() => {
                            const content = (m as ChatMessage).content;
                            const parsed = parseOrchestratorWarning(content);
                            const prose = parsed ? stripJsonBlocks(parsed.rest) : stripJsonBlocks(content);
                            const display = (prose && prose.trim()) ? prose.trim() : '';
                            return parsed ? (
                              <>
                                <div className="text-[#B45309] mb-2">{parsed.warning}</div>
                                {display ? <div>{display}</div> : null}
                              </>
                            ) : (
                              display
                            );
                          })()
                        : m.content}
                    </div>
                  </div>
                  {m.role === 'user' && (
                    <PlanningUserAvatar
                      displayName={displayName}
                      avatarUrl={avatarUrl}
                    />
                  )}
                </div>
                );
              })}
              {loading && (
                <div className="flex justify-start">
                  <div
                    className={
                      embedded
                        ? 'rounded-xl px-4 py-2 bg-white text-gray-800 text-base shadow-sm ring-1 ring-gray-200/80'
                        : 'rounded-xl px-4 py-2 bg-transparent border border-[var(--color-border)] text-[var(--color-surface-text)] text-base'
                    }
                  >
                    {t('planning.thinking')}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Error / success */}
            {error && (
              <div
                className={`${panePadX} py-2 bg-red-100 text-red-800 text-base border-t flex-shrink-0 ${
                  embedded ? 'border-gray-400/35' : 'border-[var(--color-border)]'
                }`}
              >
                {error}
              </div>
            )}

            {/* Composer dock — fixed at bottom of chat pane; only messages scroll above. */}
            <div
              className={`flex-shrink-0 w-full border-t ${panePadX} ${
                embedded
                  ? 'border-gray-400/35 bg-transparent pb-2 pt-2'
                  : 'border-[var(--color-border)] bg-[var(--color-surface-bg)] pb-3 pt-2'
              }`}
            >
              <div className="w-full">
              <form
                className="bg-transparent"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSend();
                  }}
                >
                  {(attachedDocs.length > 0 || attachingFileName) && (
                    <div className="flex flex-wrap gap-2 mb-2 px-1">
                      {attachingFileName && (
                        <span
                          className={
                            embedded
                              ? 'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-gray-800 text-sm border border-gray-400/45 bg-white/90'
                              : 'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-[var(--color-primary-bg)]/20 text-[var(--color-surface-text)] text-sm border border-[var(--color-primary-bg)]'
                          }
                        >
                          <span
                            className={`animate-spin h-3.5 w-3.5 border-2 border-t-transparent rounded-full ${
                              embedded ? 'border-gray-500' : 'border-[var(--color-primary-bg)]'
                            }`}
                            aria-hidden
                          />
                          <span className="truncate max-w-[140px]" title={attachingFileName}>
                            {t('planning.attaching', { fileName: attachingFileName })}
                          </span>
                        </span>
                      )}
                      {attachedDocs.map((d) => (
                        <span
                          key={d.id}
                          className={
                            embedded
                              ? 'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-gray-800 text-sm border border-gray-400/40 bg-white/90'
                              : 'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-[var(--color-secondary-bg)] text-[var(--color-surface-text)] text-sm border border-[var(--color-border)]'
                          }
                        >
                          <span className="truncate max-w-[140px]" title={d.fileName}>
                            {d.fileName}
                          </span>
                          <span className="text-green-600 dark:text-green-400" title={t('planning.attached')}>
                            ✓
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                  {messages.length === 0 && !isWorkspace && (
                    <h2
                      className={
                        embedded
                          ? 'mb-3 flex items-center justify-center gap-2 px-1 text-lg font-normal text-gray-700 md:text-xl'
                          : 'mb-3 flex items-center justify-start gap-2 px-1 text-lg font-normal text-[var(--color-surface-text)] md:text-xl'
                      }
                    >
                      <LightBulbIcon
                        className={
                          embedded
                            ? 'h-6 w-6 shrink-0 text-gray-600'
                            : 'h-6 w-6 shrink-0 text-[var(--color-surface-text)] opacity-80'
                        }
                        aria-hidden
                      />
                      {t('website.landing.tryAiTitle')}
                    </h2>
                  )}
                  {messages.length === 0 && (
                    <p
                      className={
                        embedded
                          ? 'mb-5 px-1 text-center text-sm text-gray-600'
                          : 'mb-5 px-1 text-left text-sm text-[var(--color-surface-text-secondary)]'
                      }
                    >
                      Build a new roadmap or share existing one to improve. Our AI will be your world class consultant!
                    </p>
                  )}
                  {/* Auth CTA: only after chatting has started, immediately above composer */}
                  {!isLoggedIn && messages.length > 0 && (
                    <PlanningAnonAuthBar embedded={embedded} />
                  )}
                  {/* Compact single-row composer: ChatGPT height, TMOS colors */}
                  <div
                    className={
                      embedded
                        ? 'flex items-end gap-1 rounded-full bg-white py-1.5 pl-1.5 pr-1.5 shadow-lg ring-1 ring-gray-200/80'
                        : 'flex items-end gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-surface-bg)] py-1.5 pl-1.5 pr-1.5 shadow-lg'
                    }
                  >
                      <input
                        ref={attachInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.json,.csv,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/json,text/csv"
                        className="hidden"
                        onChange={handleAttach}
                      />
                      <button
                        type="button"
                        onClick={() => attachInputRef.current?.click()}
                        disabled={loading || attaching}
                        title={t('planning.attachFile')}
                        className={
                          embedded
                            ? 'mb-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-200/60 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                            : 'mb-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--color-surface-text-secondary)] hover:bg-[var(--color-accent-bg)] hover:text-[var(--color-surface-text)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                        }
                        aria-label={t('planning.attachFileAria')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M12 5v14M5 12h14" />
                        </svg>
                      </button>
                    <div className="relative min-w-0 flex-1 self-center">
                      {showPlanningInputHints && (
                        <div
                          className="pointer-events-none absolute inset-0 z-0 overflow-hidden px-1 py-2"
                          aria-hidden
                        >
                          <p
                            key={planningHintIndex}
                            className={`animate-planning-hint-enter line-clamp-1 text-left text-base leading-snug ${
                              embedded
                                ? 'text-gray-500'
                                : 'text-[var(--color-surface-text-secondary)] opacity-90'
                            }`}
                          >
                            {PLANNING_INPUT_EXAMPLE_PROMPTS[planningHintIndex]}
                          </p>
                        </div>
                      )}
                      <textarea
                        ref={composerTextareaRef}
                        value={inputValue}
                        onChange={(e) => {
                          setInputValue(e.target.value);
                          const el = e.target;
                          el.style.height = 'auto';
                          el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
                        }}
                        onFocus={() => {
                          if (embedded) {
                            const sx = window.scrollX;
                            const sy = window.scrollY;
                            setComposerFocused(true);
                            if (isLoggedIn) setShouldFetchModels(true);
                            requestAnimationFrame(() => {
                              requestAnimationFrame(() => {
                                if (window.scrollX !== sx || window.scrollY !== sy) {
                                  window.scrollTo(sx, sy);
                                }
                              });
                            });
                          } else {
                            setComposerFocused(true);
                            if (isLoggedIn) setShouldFetchModels(true);
                          }
                        }}
                        onBlur={() => setComposerFocused(false)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (inputValue.trim() || attachedDocs.length > 0) {
                              handleSend();
                            }
                          }
                        }}
                        placeholder={
                          showPlanningInputHints
                            ? ''
                            : showApplySection
                              ? t('planning.placeholderAddRefinements')
                              : attachedDocs.length > 0
                                ? t('planning.placeholderAddMessage')
                                : t('planning.askAnything')
                        }
                        rows={1}
                        className={
                          showPlanningInputHints
                            ? embedded
                              ? 'relative z-10 max-h-40 w-full resize-none overflow-y-auto border-0 bg-transparent px-1 py-2 text-base leading-snug text-transparent caret-gray-800 placeholder:text-transparent focus:outline-none focus:ring-0 disabled:opacity-50'
                              : 'relative z-10 max-h-40 w-full resize-none overflow-y-auto border-0 bg-transparent px-1 py-2 text-base leading-snug text-transparent caret-[var(--color-surface-text)] placeholder:text-transparent focus:outline-none focus:ring-0 disabled:opacity-50'
                            : embedded
                              ? 'relative z-10 max-h-40 w-full resize-none overflow-y-auto border-0 bg-transparent px-1 py-2 text-base leading-snug text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-0 disabled:opacity-50'
                              : 'relative z-10 max-h-40 w-full resize-none overflow-y-auto border-0 bg-transparent px-1 py-2 text-base leading-snug text-[var(--color-surface-text)] placeholder:text-[var(--color-surface-text-secondary)] focus:outline-none focus:ring-0 disabled:opacity-50'
                        }
                        disabled={loading}
                      />
                    </div>
                      {isLoggedIn && hasOwnQuota && mergedModels.length > 0 && (
                        <select
                          id="strap-model-select"
                          value={selectedModelId}
                          onChange={(e) => void handleSetDefaultModel(e.target.value)}
                          className={
                            embedded
                              ? 'mb-0.5 min-w-0 max-w-[140px] shrink-0 px-2 py-1.5 rounded-full border border-gray-400/45 bg-transparent text-gray-900 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400/50 cursor-pointer'
                              : 'mb-0.5 min-w-0 max-w-[140px] shrink-0 px-2 py-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] text-sm focus:ring-2 focus:ring-[var(--color-primary-text)] cursor-pointer'
                          }
                          aria-label={t('planning.chooseModel')}
                        >
                          {mergedModels.map((m) => (
                            <option
                              key={m.id}
                              value={m.id}
                              disabled={m.usageExhausted || !m.isActive}
                              className={m.usageExhausted ? 'text-gray-400' : ''}
                            >
                              {m.source} — {m.displayName}
                              {m.usageExhausted ? ` ${t('planning.exhausted')}` : ''}
                            </option>
                          ))}
                        </select>
                      )}
                      <button
                        type="submit"
                        disabled={loading || (isLoggedIn && !sessionId) || (!inputValue.trim() && attachedDocs.length === 0)}
                        title={t('planning.send')}
                        aria-label={t('planning.send')}
                        className={
                          embedded
                            ? 'mb-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-900 text-white disabled:opacity-40 hover:bg-gray-800 transition-colors'
                            : 'mb-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] disabled:opacity-40 hover:opacity-90 transition-opacity'
                        }
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M12 19V5M5 12l7-7 7 7" />
                        </svg>
                      </button>
                  </div>
                </form>
              </div>
            </div>
            {embedCenterComposer && (
              <div className="min-h-0 w-full min-w-0 flex-1 shrink" aria-hidden />
            )}
          </div>
        </div>

        {/* Preview pane — full width of right column in workspace split */}
        <div
          className={`min-h-0 min-w-0 h-full max-h-full w-full flex-col overflow-hidden ${
            viewMode === 'preview' ? 'flex' : 'hidden'
          } ${splitDesktop ? 'lg:flex lg:h-full' : 'flex-1'} ${
            embedded
              ? 'min-h-[200px] bg-transparent rounded-xl border border-gray-400/45'
              : 'rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)]'
          }`}
        >
          <div
            className={
              embedded
                ? 'flex flex-col flex-1 min-w-0 min-h-0 bg-transparent'
                : 'flex flex-col flex-1 min-w-0 min-h-0 bg-[var(--color-secondary-bg)]'
            }
          >
          <div className={paneHeaderClass}>
            <h2 className={paneTitleClass}>{t('planning.preview')}</h2>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !draft}
                className={paneHeaderButtonClass}
              >
                {saving ? t('planning.saving') : t('planning.saveRoadmap')}
              </button>
              <div className="relative" ref={exportDropdownRef}>
                <button
                  type="button"
                  onClick={() => setExportDropdownOpen((o) => !o)}
                  disabled={!draft}
                  className={paneHeaderButtonClass}
                >
                  {t('planning.export')}
                  <ChevronDownIcon className={`h-4 w-4 transition-transform ${exportDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {exportDropdownOpen && draft && (
                  <div
                    className={
                      embedded
                        ? 'absolute right-0 top-full mt-1 py-1 min-w-[120px] rounded-md border border-gray-400/45 bg-gray-100 shadow-md z-10'
                        : 'absolute right-0 top-full mt-1 py-1 min-w-[120px] rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] shadow-lg z-10'
                    }
                  >
                    {(['text', 'pdf', 'docx'] as const).map((fmt) => (
                      <button
                        key={fmt}
                        type="button"
                        onClick={() => {
                          setExportDropdownOpen(false);
                          handleDownload(fmt);
                        }}
                        className={
                          embedded
                            ? 'w-full text-left px-3 py-2 text-base text-gray-900 hover:bg-gray-200/50'
                            : 'w-full text-left px-3 py-2 text-base text-[var(--color-surface-text)] hover:bg-[var(--color-surface-hover)]'
                        }
                      >
                        {fmt === 'text' ? t('planning.formatText') : fmt === 'pdf' ? t('planning.formatPdf') : t('planning.formatDocx')}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          {saveSuccess && (
            <div
              className={`px-3 py-2 bg-green-100 text-green-800 text-base border-b flex-shrink-0 flex flex-wrap items-center gap-2 ${
                embedded ? 'border-gray-400/35' : 'border-[var(--color-border)]'
              }`}
            >
              <span>{saveSuccess}</span>
              {savedRoadmapId && (
                <>
                  <a
                    href={`/tenant/roadmaps/builder?roadmap=${savedRoadmapId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/tenant/roadmaps/builder?roadmap=${savedRoadmapId}`);
                    }}
                    className="font-medium underline hover:no-underline"
                  >
                    {t('planning.viewRoadmap')}
                  </a>
                  <span className="text-green-600">·</span>
                  <a
                    href="/tenant/roadmaps"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/tenant/roadmaps');
                    }}
                    className="font-medium underline hover:no-underline"
                  >
                    {t('planning.myRoadmaps')}
                  </a>
                </>
              )}
            </div>
          )}
          <div
            ref={rightPanelScrollRef}
            className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain"
          >
            {draft ? (
              <>
                <div className="p-3 pt-3 pb-3">
                  <StructureTreeBlock
                    draft={draft}
                    showStatus={draftHasAnyStatus(draft)}
                    expanded={structureExpanded}
                    onToggle={() => setStructureExpanded((e) => !e)}
                  />
                </div>
                <div
                  className={`border-t ${
                    embedded ? 'border-gray-400/35' : 'border-[var(--color-border)]'
                  }`}
                >
                  <PlanningDraftVisualView draft={draft} ganttChartRef={ganttChartRef} />
                  <DraftReferencesBlock draft={draft} />
                </div>
              </>
            ) : (
              <div className="flex min-h-[200px] items-center justify-center p-6 text-center">
                <p
                  className={
                    embedded
                      ? 'text-sm text-gray-600 max-w-xs'
                      : 'text-sm text-[var(--color-surface-text-secondary)] max-w-xs'
                  }
                >
                  {t('planning.previewEmpty')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>

      {/* Save/Export gating modal – unauthenticated users must sign up */}
      {saveGatingOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-xl p-6 max-w-md border border-[var(--color-border)]">
            <h3 className="text-lg font-semibold text-[var(--color-surface-text)]">{t('planning.signUpRequired')}</h3>
            <p className="mt-2 text-base text-[var(--color-surface-text)]">
              {t('planning.signUpRequiredDesc')}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSaveGatingOpen(false)}
                className="px-3 py-1.5 rounded border border-[var(--color-border)] text-[var(--color-surface-text)]"
              >
                {t('common.cancel')}
              </button>
              <a
                href="/register"
                className="px-3 py-1.5 rounded bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]"
              >
                {t('planning.signUp')}
              </a>
              <a
                href="/"
                className="px-3 py-1.5 rounded border border-[var(--color-border)] text-[var(--color-surface-text)]"
              >
                {t('planning.signIn')}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Delete conversation confirmation */}
      {deleteConfirmSessionId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-xl p-6 max-w-md border border-[var(--color-border)]">
            <h3 className="text-lg font-semibold text-[var(--color-surface-text)]">{t('planning.deleteConversation')}</h3>
            <p className="mt-2 text-base text-[var(--color-surface-text)]">
              {t('planning.deleteConversationDesc')}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmSessionId(null)}
                disabled={deleting}
                className="px-3 py-1.5 rounded border border-[var(--color-border)] text-[var(--color-surface-text)] disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? t('planning.deleting') : t('planning.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanningChatPage;
